/**
 * Scenery visuals.
 *
 * Props are the difference between a grid of identical rooms and somewhere you
 * have been before, so what matters about them is not detail — at this light
 * level nothing but the silhouette survives — but *recognition*. A room with an
 * overturned chair in it is a room you can navigate back to. That is the whole
 * job, and it sets every constraint here:
 *
 *  - **Static.** `place` runs once at level build and nothing touches a prop
 *    again. There is no update path, so there is no per-frame cost.
 *  - **Cheap.** A level carries 150-400 of these. A chair is a seat, a back and
 *    four legs; each kind merges down to one or two draw calls and a few dozen
 *    triangles, and every instance of a kind shares the same buffers.
 *  - **Local.** Nothing has an absolute colour. Every material is pulled toward
 *    the level's own air by `propPalette`, so a cardboard box is warm brown in
 *    the lobby and grey-green in the poolrooms rather than looking imported.
 *  - **Varied without cost.** Each kind bakes a handful of poses — upright,
 *    tipped, on its side, half emptied — and `PropSpot.variant` picks one, plus
 *    a deterministic wobble on the yaw. No allocation, no runtime maths.
 *
 * The landmark kinds (`graffiti`, `tally-marks`, `arrow`, `sign`, `camp`) are
 * the ones that carry the level's only human presence. Their surfaces are drawn
 * on a canvas in `propdecals.ts`, because handwriting is a hand and a hand does
 * not survive being modelled out of boxes.
 *
 * Two later groups have rules of their own.
 *
 *  - **Architecture** (`half-wall` through `inner-window`) is made of the
 *    building rather than of props: `propwalls.ts` hands out the level's own
 *    wall surface, its skirting board and the plain core that shows where the
 *    building has been cut. A half-wall in plain grey reads as a crate; in the
 *    room's own wallpaper, with the room's own skirting along its foot and a
 *    raw end standing in open floor, it reads as something that was built and
 *    then abandoned. That is the entire effect.
 *  - **Objects** (`bathtub` through `bicycle`) are solid — they carry a
 *    `footprint` in `PROP_DEFS` and the player collides with them — so a model
 *    that disagrees with its box is a bug the player feels as an invisible
 *    wall. Everything below is therefore centred on its local origin in X and Z
 *    and sized to the box, and where a variant has been knocked over it is
 *    re-centred (`centreZ`) rather than left lying outside it.
 */

import * as THREE from 'three';
import { makeRNG } from '../core/rng';
import type { LevelSpec, PropKind, PropSpot } from '../core/types';
import type { PropView } from './api';
import type { AtlasEntry, DecalAtlas } from './propdecals';
import {
  makeArrowAtlas,
  makeClockFace,
  makeGraffitiAtlas,
  makePuddleTexture,
  makeSignAtlas,
  makeStainTexture,
  makeTallyAtlas,
  SIGN_ROW,
} from './propdecals';
import {
  anchorProp,
  belong,
  box,
  css,
  cyl,
  makeShape,
  matte,
  merge,
  mergeUV,
  propPalette,
  settle,
  slab,
  SpecKit,
  strut,
  tumble,
  variantIndex,
  wallDecal,
  type Anchor,
  type PropLayer,
  type PropPalette,
  type PropShape,
} from './propkit';
import { buildSlide } from './propslide';
import {
  acquireArch,
  addParts,
  buildingColumn,
  buildingWall,
  face,
  PAPER_METRES,
  releaseArch,
  WALL_INSET,
  type ArchKit,
  type WallParts,
} from './propwalls';
import {
  buildArcadeCabinet,
  buildBallPit,
  buildBalloons,
  buildBanner,
  buildBouncyCastle,
  buildConfetti,
  buildCrawlTube,
  buildPartyTable,
  buildPlaySlide,
  buildPlayStructure,
  buildSoftBlocks,
  buildStreamers,
} from './propfun';
import { buildHouseHill, buildHouseRoof, buildHouseTrim } from './propsubhouse';
import {
  buildGardenTree,
  buildLampPost,
  buildLaundryLine,
  buildMailbox,
  buildParkedCar,
  buildPicketFence,
  buildShrub,
  buildSwingSet,
  buildWaterTower,
} from './propsuburb';

/* ------------------------------------------------------------------ *
 * Local geometry helpers
 * ------------------------------------------------------------------ */

/** A box turned about its own vertical axis before being put in place. */
function ybox(w: number, h: number, d: number, x: number, y: number, z: number, yaw = 0): THREE.BufferGeometry {
  const g = new THREE.BoxGeometry(w, h, d);
  if (yaw) g.rotateY(yaw);
  g.translate(x, y, z);
  return g;
}

/** A capped cylinder lying along X: rungs, tubes, anything crossing a frame. */
function rodX(r: number, len: number, seg: number, x: number, y: number, z: number): THREE.BufferGeometry {
  const g = new THREE.CylinderGeometry(r, r, len, seg, 1);
  g.rotateZ(Math.PI / 2);
  g.translate(x, y, z);
  return g;
}

/** A capped cylinder lying along Z: anything sticking out of a wall. */
function rodZ(r: number, len: number, seg: number, x: number, y: number, z: number): THREE.BufferGeometry {
  const g = new THREE.CylinderGeometry(r, r, len, seg, 1);
  g.rotateX(Math.PI / 2);
  g.translate(x, y, z);
  return g;
}

/**
 * An *open* cylinder lying along Z: a rim, not a disc.
 *
 * A bezel built with `rodZ` is capped, and the cap is an opaque disc sitting in
 * front of whatever the bezel is supposed to be framing — which is how the wall
 * clock came to render as a featureless dark circle.
 */
function tubeZ(r: number, len: number, seg: number, x: number, y: number, z: number): THREE.BufferGeometry {
  const g = new THREE.CylinderGeometry(r, r, len, seg, 1, true);
  g.rotateX(Math.PI / 2);
  g.translate(x, y, z);
  return g;
}

function layer(material: THREE.Material, variants: Array<THREE.BufferGeometry | null>): PropLayer {
  return { material, variants };
}

/** Shorthand for a kind whose every variant is the same single layer. */
function solo(material: THREE.Material, variants: Array<THREE.BufferGeometry | null>): PropLayer[] {
  return [layer(material, variants)];
}

/**
 * A ring. Upright in the XY plane by default — bicycle wheels, chainrings — or
 * `flat` in the XZ plane for anything hanging.
 */
function ring(radius: number, tube: number, seg: number, x = 0, y = 0, z = 0, flat = false): THREE.BufferGeometry {
  const g = new THREE.TorusGeometry(radius, tube, 3, seg);
  if (flat) g.rotateX(Math.PI / 2);
  if (x || y || z) g.translate(x, y, z);
  return g;
}

/**
 * Where local `(0, 0, -d)` ends up once a part has been turned `yaw` about Y.
 * The front of a box that has been given a wobble is the one offset that keeps
 * getting written down backwards.
 */
function faceOffset(yaw: number, d: number): { dx: number; dz: number } {
  return { dx: -Math.sin(yaw) * d, dz: -Math.cos(yaw) * d };
}

/** A low-poly blob. Heads, bonnets, cushions — anything that must not be a box. */
function blob(rx: number, ry: number, rz: number, x = 0, y = 0, z = 0): THREE.BufferGeometry {
  const g = new THREE.SphereGeometry(1, 7, 5);
  g.scale(rx, ry, rz);
  g.translate(x, y, z);
  return g;
}

/**
 * A box leaned about X and dropped back onto the floor. Everything that rests
 * against a wall — doors, mattresses, mirrors, a bike — is this, and doing it
 * here keeps the lean out of the object transform so the geometry stays shared.
 */
function lean(parts: readonly THREE.BufferGeometry[], angle: number, z: number): void {
  for (const g of parts) {
    g.rotateX(angle);
    g.translate(0, 0, z);
  }
  settle(parts);
}

/**
 * Slide geometries together until they straddle z = 0.
 *
 * Anything knocked over lands off to one side of where it stood, and its
 * collision box in `PROP_DEFS` does not move with it. Re-centring is what keeps
 * a fallen prop inside the box the player is actually stopped by.
 */
function centreZ(geos: readonly THREE.BufferGeometry[]): void {
  let min = Infinity;
  let max = -Infinity;
  for (const g of geos) {
    g.computeBoundingBox();
    if (!g.boundingBox) continue;
    min = Math.min(min, g.boundingBox.min.z);
    max = Math.max(max, g.boundingBox.max.z);
  }
  if (!Number.isFinite(min)) return;
  const dz = -(min + max) / 2;
  for (const g of geos) g.translate(0, 0, dz);
}

/** Glass that is looked *through* rather than at: dry tanks, booths, mirrors. */
function pane(color: THREE.Color, opacity: number): THREE.MeshLambertMaterial {
  return new THREE.MeshLambertMaterial({
    color,
    emissive: color,
    emissiveIntensity: 0.04,
    transparent: true,
    opacity,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
}

/* ------------------------------------------------------------------ *
 * Decal helpers
 * ------------------------------------------------------------------ */

/**
 * Writing on a wall. Alpha-tested rather than blended: marker and scratches are
 * hard-edged anyway, and it keeps thirty decals out of the transparency sort.
 */
function inkMaterial(map: THREE.Texture): THREE.MeshLambertMaterial {
  return new THREE.MeshLambertMaterial({ map, alphaTest: 0.08 });
}

/** A soft-edged stain. Blended, and never writes depth. */
function stainMaterial(map: THREE.Texture): THREE.MeshLambertMaterial {
  return new THREE.MeshLambertMaterial({
    map,
    transparent: true,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
  });
}

/** The ink a level's walls take: dark on pale wallpaper, chalk on dark concrete. */
function inkColours(pal: PropPalette): { ink: string; lip: string } {
  const soft = pal.pull * 0.4;
  return pal.lightWalls
    ? { ink: css(belong(0x1b1916, pal, soft, 1)), lip: css(belong(0xf0e9d6, pal, soft, 1)) }
    : { ink: css(belong(0xd7d0be, pal, soft, 1)), lip: css(belong(0x141210, pal, soft, 1)) };
}

/* ================================================================== *
 * Lobby
 * ================================================================== */

/**
 * A stacking chair, and the four ways one ends up. The fall is baked into the
 * geometry and the result settled back onto the floor, so a chair on its side
 * rests on its side instead of hovering at the angle it was rotated to.
 */
function buildChair(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const shell: Array<THREE.BufferGeometry | null> = [];
  const frame: Array<THREE.BufferGeometry | null> = [];

  // rx, rz, ry
  const poses: ReadonlyArray<readonly [number, number, number]> = [
    [0, 0, 0],
    [-1.1, 0.06, 0.2],
    [0.05, 1.48, -0.35],
    [Math.PI, 0.22, 0.6],
  ];

  for (const [rx, rz, ry] of poses) {
    const s = merge([box(0.44, 0.045, 0.42, 0, 0.45, 0), box(0.42, 0.42, 0.035, 0, 0.67, 0.19)]);
    const f = merge([
      box(0.035, 0.45, 0.035, -0.18, 0.225, -0.17),
      box(0.035, 0.45, 0.035, 0.18, 0.225, -0.17),
      box(0.035, 0.45, 0.035, -0.18, 0.225, 0.17),
      box(0.035, 0.45, 0.035, 0.18, 0.225, 0.17),
      box(0.4, 0.022, 0.022, 0, 0.14, 0.17),
    ]);
    if (rx || rz || ry) tumble([s, f], rx, rz, ry);
    settle([s, f]);
    shell.push(s);
    frame.push(f);
  }

  return makeShape(spec, { kind: 'floor', yawJitter: 0.6, tilt: 0.015 }, [
    layer(matte(pal.plastic), shell),
    layer(matte(pal.metalDark), frame),
  ]);
}

/** Cardboard, stacked by someone who meant to come back for it. */
function buildBoxes(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);

  const stackTwo = merge([ybox(0.5, 0.36, 0.42, 0, 0.18, 0), ybox(0.44, 0.3, 0.38, 0.03, 0.51, -0.02, 0.34)]);
  const stackThree = merge([
    ybox(0.52, 0.34, 0.44, 0, 0.17, 0),
    ybox(0.46, 0.3, 0.4, -0.03, 0.49, 0.03, -0.28),
    ybox(0.34, 0.26, 0.32, 0.04, 0.77, -0.02, 0.52),
  ]);

  /**
   * One flap of an opened box. The panel is built with its hinge edge on the
   * origin and its free edge pointing outward, so rotating about the hinge and
   * then dropping it on the rim is all it takes to have it stand open.
   */
  const flap = (
    span: number,
    axis: 'x' | 'z',
    reach: number,
    swing: number,
    hx: number,
    hz: number
  ): THREE.BufferGeometry => {
    const d = Math.abs(reach);
    if (axis === 'z') {
      const g = box(span, 0.012, d, 0, 0, reach / 2);
      g.rotateX(swing);
      g.translate(hx, 0.34, hz);
      return g;
    }
    const g = box(d, 0.012, span, reach / 2, 0, 0);
    g.rotateZ(swing);
    g.translate(hx, 0.34, hz);
    return g;
  };
  const opened = merge([
    ybox(0.48, 0.34, 0.44, 0, 0.17, 0),
    flap(0.46, 'z', -0.2, 0.95, 0, -0.22),
    flap(0.46, 'z', 0.2, -0.8, 0, 0.22),
    flap(0.42, 'x', -0.2, -0.9, -0.24, 0),
    flap(0.42, 'x', 0.2, 0.75, 0.24, 0),
  ]);

  const crushed = merge([
    ybox(0.52, 0.16, 0.44, 0, 0.08, 0, 0.12),
    ybox(0.44, 0.32, 0.4, 0.1, 0.24, -0.34, -0.4),
  ]);
  tumble([crushed], 0.05, 0.08);
  settle([crushed]);

  return makeShape(spec, { kind: 'floor', yawJitter: 0.9 }, solo(matte(pal.cardboard), [stackTwo, stackThree, opened, crushed]));
}

/** A filing cabinet. Somebody went through it before you did. */
function buildCabinet(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const drawerY = [0.28, 0.68, 1.08];

  const body = () => box(0.46, 1.3, 0.62, 0, 0.65, -0.31);
  const front = (y: number, out: number) => box(0.4, 0.36, 0.03, 0, y, -0.625 - out);
  const handle = (y: number, out: number) => box(0.16, 0.025, 0.03, 0, y + 0.12, -0.65 - out);

  const shellClosed = merge([body(), ...drawerY.map((y) => front(y, 0))]);
  const detailClosed = merge(drawerY.map((y) => handle(y, 0)));

  const shellOpen = merge([body(), front(drawerY[0]!, 0), front(drawerY[1]!, 0), front(drawerY[2]!, 0.3)]);
  const detailOpen = merge([
    handle(drawerY[0]!, 0),
    handle(drawerY[1]!, 0),
    handle(drawerY[2]!, 0.3),
    // The drawer body pulled out behind its front, and the dark it came from.
    box(0.38, 0.32, 0.3, 0, drawerY[2]!, -0.77),
    box(0.4, 0.34, 0.02, 0, drawerY[2]!, -0.6),
  ]);

  const shellGone = merge([body(), front(drawerY[0]!, 0), front(drawerY[1]!, 0)]);
  const detailGone = merge([handle(drawerY[0]!, 0), handle(drawerY[1]!, 0), box(0.4, 0.36, 0.04, 0, drawerY[2]!, -0.6)]);

  return makeShape(spec, { kind: 'floor', yawJitter: 0.1 }, [
    layer(matte(pal.metal), [shellClosed, shellOpen, shellGone]),
    layer(matte(pal.dark), [detailClosed, detailOpen, detailGone]),
  ]);
}

/** A mop bucket. The mop is usually still in it. */
function buildBucket(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const shell = () => [cyl(0.175, 0.13, 0.3, 8, 0, 0.15, 0), cyl(0.185, 0.18, 0.025, 8, 0, 0.3, 0)];

  const upright = merge(shell());
  const tipped = merge(shell());
  tumble([tipped], 0.1, 1.5, 0.3);
  settle([tipped]);
  const withMop = merge(shell());

  // The mop is still in it, leaning on the rim the way it was left.
  const handle = merge([
    strut(0.016, 0.02, 0.2, 0.02, 0.34, 1.35, 0.22, 5),
    cyl(0.075, 0.1, 0.16, 6, 0.02, 0.11, 0.02),
  ]);

  return makeShape(spec, { kind: 'floor', yawJitter: 1.2, tilt: 0.02 }, [
    layer(matte(pal.plastic), [upright, tipped, withMop]),
    layer(matte(pal.wood), [null, null, handle]),
  ]);
}

/** Paper, shed across the carpet. Two triangles a sheet. */
function buildPapers(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const variants: Array<THREE.BufferGeometry | null> = [];
  for (let v = 0; v < 3; v++) {
    const rng = makeRNG(0x50a7 + v * 977);
    const parts: THREE.BufferGeometry[] = [];
    const n = 5 + v * 2;
    for (let i = 0; i < n; i++) {
      const s = rng.range(0.85, 1.15);
      parts.push(
        slab(
          0.21 * s,
          0.297 * s,
          rng.range(-0.34, 0.34),
          0.004 + i * 0.0016,
          rng.range(-0.34, 0.34),
          rng.range(0, Math.PI * 2)
        )
      );
    }
    variants.push(merge(parts));
  }
  return makeShape(spec, { kind: 'floor', out: 0.002 }, solo(matte(pal.paper, 0.09), variants));
}

/**
 * A vending machine, still lit. It is the only thing in the level that looks
 * like it is waiting for someone, which is why the sign stays on in both
 * variants — a lit machine in an empty room is the landmark.
 */
function buildVending(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const shell = () => [box(1.0, 1.95, 0.72, 0, 0.98, -0.36), box(1.04, 0.08, 0.76, 0, 0.04, -0.38)];

  const intact = merge(shell());
  const smashed = merge(shell());

  const darkIntact = merge([
    box(0.6, 0.93, 0.02, -0.16, 1.21, -0.73),
    box(0.2, 0.85, 0.02, 0.35, 1.25, -0.73),
    box(0.46, 0.18, 0.05, -0.16, 0.42, -0.72),
  ]);
  const darkSmashed = merge([
    // The glass is gone; what is left is the dark behind it and empty racks.
    box(0.6, 0.93, 0.06, -0.16, 1.21, -0.66),
    box(0.2, 0.85, 0.02, 0.35, 1.25, -0.73),
    box(0.46, 0.18, 0.05, -0.16, 0.42, -0.72),
    box(0.56, 0.015, 0.16, -0.16, 0.9, -0.68),
    box(0.56, 0.015, 0.16, -0.16, 1.2, -0.68),
    box(0.56, 0.015, 0.16, -0.16, 1.5, -0.68),
  ]);

  const sign = () => box(0.88, 0.2, 0.02, 0, 1.79, -0.735);
  const glow = new THREE.MeshLambertMaterial({
    color: pal.pale,
    emissive: spec.palette.lightTint,
    emissiveIntensity: 0.5,
  });

  return makeShape(spec, { kind: 'floor', yawJitter: 0.06 }, [
    layer(matte(pal.plastic), [intact, smashed]),
    layer(matte(pal.glass, 0.02), [darkIntact, darkSmashed]),
    layer(glow, [sign(), sign()]),
  ]);
}

/** A payphone. In two of the three variants the handset is off the hook. */
function buildPayphone(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const shell = () => [box(0.3, 0.5, 0.09, 0, 0, -0.045), box(0.34, 0.05, 0.13, 0, 0.27, -0.06)];

  const hung = merge([
    box(0.19, 0.13, 0.02, 0.03, 0.02, -0.1),
    box(0.07, 0.22, 0.055, -0.19, -0.06, -0.075),
    strut(0.008, -0.14, 0.06, -0.06, -0.19, 0.04, -0.07, 4),
  ]);
  const dangling = merge([
    box(0.19, 0.13, 0.02, 0.03, 0.02, -0.1),
    box(0.07, 0.22, 0.055, -0.06, -0.52, -0.07),
    strut(0.008, -0.13, -0.16, -0.05, -0.11, -0.32, -0.09, 4),
    strut(0.008, -0.11, -0.32, -0.09, -0.06, -0.42, -0.07, 4),
  ]);
  const cut = merge([
    box(0.19, 0.13, 0.02, 0.03, 0.02, -0.1),
    strut(0.008, -0.13, -0.16, -0.05, -0.15, -0.34, -0.06, 4),
  ]);

  return makeShape(spec, { kind: 'wall', height: 1.35 }, [
    layer(matte(pal.metalDark), [merge(shell()), merge(shell()), merge(shell())]),
    layer(matte(pal.dark), [hung, dangling, cut]),
  ]);
}

/**
 * A fixture that came down and stayed down. The tube is faintly self-lit but
 * emits no light — the light rig owns illumination; this only has to be visible
 * enough to be a landmark when the torch crosses it.
 */
function buildHangingLight(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const DROP = 0.5;

  /** Where a cable meets the fixture once the fixture has been tilted by `t`. */
  const mount = (sx: number, t: number): [number, number] => [
    sx * Math.cos(t) - 0.05 * Math.sin(t),
    sx * Math.sin(t) + 0.05 * Math.cos(t) - DROP,
  ];

  const fixture = (t: number): { metal: THREE.BufferGeometry; tube: THREE.BufferGeometry } => {
    const shellParts = [box(1.16, 0.09, 0.16, 0, 0, 0), box(1.16, 0.04, 0.2, 0, 0.05, 0)];
    const tubeParts = [rodX(0.032, 1.02, 6, 0, -0.05, 0)];
    tumble([...shellParts, ...tubeParts], 0, t);
    const shift = (g: THREE.BufferGeometry) => g.translate(0, -DROP, 0);
    shellParts.forEach(shift);
    tubeParts.forEach(shift);
    return { metal: merge(shellParts), tube: merge(tubeParts) };
  };

  const level = fixture(0.11);
  const [lax, lay] = mount(-0.5, 0.11);
  const [lbx, lby] = mount(0.5, 0.11);
  const levelMetal = merge([level.metal, strut(0.007, -0.5, 0, 0, lax, lay, 0, 4), strut(0.007, 0.5, 0, 0, lbx, lby, 0, 4)]);

  const askew = fixture(0.62);
  const [aax, aay] = mount(-0.5, 0.62);
  const askewMetal = merge([
    askew.metal,
    strut(0.007, -0.5, 0, 0, aax, aay, 0, 4),
    // The other cable let go and hangs on its own.
    strut(0.007, 0.5, 0, 0, 0.56, -0.82, 0.05, 4),
  ]);

  const bare = merge([
    strut(0.007, -0.06, 0, 0, -0.02, -0.44, 0.01, 4),
    cyl(0.032, 0.026, 0.09, 6, -0.02, -0.49, 0.01),
  ]);
  const bulb = new THREE.SphereGeometry(0.045, 6, 4);
  bulb.translate(-0.02, -0.56, 0.01);

  const tubeMat = new THREE.MeshLambertMaterial({
    color: pal.pale,
    emissive: spec.palette.lightTint,
    emissiveIntensity: 0.35,
  });

  return makeShape(spec, { kind: 'ceiling', yawJitter: 0.5 }, [
    layer(matte(pal.metalDark), [levelMetal, askewMetal, bare]),
    layer(tubeMat, [level.tube, askew.tube, bulb]),
  ]);
}

/** Water damage on the ceiling tiles. A plane and a canvas, nothing more. */
function buildCeilingStain(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const texture = makeStainTexture(css(pal.grime), 0x3ce17b, 0.62);
  const sizes: ReadonlyArray<readonly [number, number]> = [
    [1.3, 1.0],
    [1.9, 1.5],
    [0.9, 1.1],
  ];
  const variants = sizes.map(([w, d]) => {
    const g = new THREE.PlaneGeometry(w, d);
    // Face down: +Z becomes -Y.
    g.rotateX(Math.PI / 2);
    return g;
  });
  return makeShape(
    spec,
    { kind: 'ceiling', out: 0.02, yawJitter: Math.PI },
    solo(stainMaterial(texture), variants),
    [texture]
  );
}

/* ================================================================== *
 * Warehouse
 * ================================================================== */

/** A pallet: three stringers, a deck, and the ways one gets left. */
function buildPallet(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);

  const deckBoards = (count: number) => {
    const parts: THREE.BufferGeometry[] = [];
    for (let i = 0; i < count; i++) {
      const x = -0.55 + (i / Math.max(1, count - 1)) * 1.1;
      parts.push(box(0.11, 0.022, 0.82, x, 0.111, 0));
    }
    return parts;
  };
  const frame = () => [
    box(1.2, 0.09, 0.1, 0, 0.055, -0.35),
    box(1.2, 0.09, 0.1, 0, 0.055, 0),
    box(1.2, 0.09, 0.1, 0, 0.055, 0.35),
    box(1.2, 0.022, 0.1, 0, 0.011, -0.35),
    box(1.2, 0.022, 0.1, 0, 0.011, 0.35),
  ];

  const flat = merge([...frame(), ...deckBoards(5)]);

  const stacked = merge([
    ...frame(),
    ...deckBoards(5),
    ...frame().map((g) => g.translate(0.02, 0.135, -0.01)),
    ...deckBoards(5).map((g) => g.translate(0.02, 0.135, -0.01)),
  ]);

  const leaning = merge([...frame(), ...deckBoards(5)]);
  // Stood on its long edge and tipped back against whatever it was leant on.
  tumble([leaning], -1.36, 0.05, 0.08);
  settle([leaning]);

  const broken = merge([
    ...frame(),
    ...deckBoards(3),
    // One board off the deck entirely, lying where it fell.
    ybox(0.11, 0.022, 0.8, 0.78, 0.011, 0.16, 0.5),
  ]);

  return makeShape(spec, { kind: 'floor', yawJitter: 0.7 }, solo(matte(pal.wood), [flat, stacked, leaning, broken]));
}

/** A wooden crate, sometimes still shut. */
function buildCrate(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const body = (h: number, y = 0) => box(0.66, h, 0.66, 0, y + h / 2, 0);
  const battens = (h: number, y = 0) => [
    box(0.06, h, 0.06, -0.32, y + h / 2, -0.32),
    box(0.06, h, 0.06, 0.32, y + h / 2, -0.32),
    box(0.06, h, 0.06, -0.32, y + h / 2, 0.32),
    box(0.06, h, 0.06, 0.32, y + h / 2, 0.32),
    box(0.7, 0.05, 0.05, 0, y + h - 0.06, -0.33),
    box(0.7, 0.05, 0.05, 0, y + 0.08, -0.33),
  ];

  const single = merge([body(0.58)]);
  const singleBat = merge(battens(0.58));

  const twoUp = merge([body(0.58), ybox(0.62, 0.5, 0.62, 0.04, 0.83, -0.03, 0.2)]);
  const twoUpBat = merge(battens(0.58));

  const openBody = merge([body(0.52)]);
  const openBat = merge([
    ...battens(0.52),
    // The lid, taken off and dropped back on at an angle.
    ybox(0.7, 0.045, 0.7, 0.12, 0.56, 0.06, 0.22),
  ]);

  const sideBody = merge([body(0.58)]);
  const sideBat = merge(battens(0.58));
  tumble([sideBody, sideBat], 0.02, 1.52, 0.4);
  settle([sideBody, sideBat]);

  return makeShape(spec, { kind: 'floor', yawJitter: 0.8 }, [
    layer(matte(pal.wood), [single, twoUp, openBody, sideBody]),
    layer(matte(pal.woodDark), [singleBat, twoUpBat, openBat, sideBat]),
  ]);
}

/** Industrial racking. Mostly empty, which is the point. */
function buildShelving(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const H = 2.1;
  const uprights = () => [
    box(0.05, H, 0.05, -0.85, H / 2, -0.04),
    box(0.05, H, 0.05, 0.85, H / 2, -0.04),
    box(0.05, H, 0.05, -0.85, H / 2, -0.46),
    box(0.05, H, 0.05, 0.85, H / 2, -0.46),
    strut(0.018, -0.85, 0.15, -0.46, 0.85, 1.95, -0.46, 4),
  ];
  const shelf = (y: number) => box(1.78, 0.03, 0.48, 0, y, -0.25);

  const empty = merge([...uprights(), shelf(0.35), shelf(0.95), shelf(1.55), shelf(2.06)]);
  const loaded = merge([...uprights(), shelf(0.35), shelf(0.95), shelf(1.55), shelf(2.06)]);
  const cargo = merge([
    ybox(0.42, 0.3, 0.36, -0.5, 0.52, -0.25, 0.12),
    ybox(0.34, 0.26, 0.32, 0.02, 0.5, -0.24, -0.3),
    ybox(0.38, 0.28, 0.34, 0.52, 1.12, -0.26, 0.24),
  ]);

  // A shelf that gave way. Whatever was on it is not here now.
  const bentShelf = box(1.78, 0.03, 0.48);
  bentShelf.rotateZ(0.2);
  bentShelf.translate(0.04, 0.86, -0.25);
  const collapsed = merge([...uprights(), shelf(0.35), shelf(1.55), shelf(2.06), bentShelf]);

  // Barely any wobble: this is authored with its back on the wall plane and it
  // is nearly two metres wide, so a degree of yaw swings one end into the wall.
  return makeShape(spec, { kind: 'floor', yawJitter: 0.03 }, [
    layer(matte(pal.metal), [empty, loaded, collapsed]),
    layer(matte(pal.cardboard), [null, cargo, null]),
  ]);
}

/** A traffic cone. The only saturated thing in the room, so it reads as a mark. */
function buildCone(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const shell = () => [box(0.3, 0.03, 0.3, 0, 0.015, 0), cyl(0.02, 0.15, 0.52, 8, 0, 0.29, 0)];
  const band = () => cyl(0.075, 0.1, 0.1, 8, 0, 0.3, 0, true);

  const upA = merge(shell());
  const bandA = band();

  const downB = merge(shell());
  const bandB = band();
  tumble([downB, bandB], 1.5, 0.1, 0.4);
  settle([downB, bandB]);

  const leanC = merge(shell());
  const bandC = band();
  tumble([leanC, bandC], 0.12, 0.42);
  settle([leanC, bandC]);

  return makeShape(spec, { kind: 'floor', yawJitter: 1.4 }, [
    layer(matte(pal.hazard), [upA, downB, leanC]),
    layer(matte(pal.pale), [bandA, bandB, bandC]),
  ]);
}

/** A steel drum. Upright, opened, or rolled onto its side. */
function buildDrum(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const shell = (lid: boolean) => {
    const parts = [
      cyl(0.29, 0.29, 0.86, 10, 0, 0.43, 0),
      cyl(0.305, 0.305, 0.05, 10, 0, 0.26, 0, true),
      cyl(0.305, 0.305, 0.05, 10, 0, 0.6, 0, true),
    ];
    if (lid) parts.push(cyl(0.3, 0.3, 0.03, 10, 0, 0.865, 0));
    return parts;
  };

  const closed = merge(shell(true));

  const opened = merge(shell(false));
  const openDark = merge([
    cyl(0.265, 0.265, 0.02, 10, 0, 0.8, 0),
    // The lid, dropped beside it.
    cyl(0.3, 0.3, 0.02, 10, 0.62, 0.01, 0.14),
  ]);

  const rolled = merge(shell(true));
  tumble([rolled], 0.06, Math.PI / 2, 0.3);
  settle([rolled]);

  return makeShape(spec, { kind: 'floor', yawJitter: 1.5 }, [
    layer(matte(pal.rust), [closed, opened, rolled]),
    layer(matte(pal.dark), [null, openDark, null]),
  ]);
}

/** A sack barrow, left where it was last used. */
function buildHandtruck(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const frame = () => [
    box(0.045, 1.12, 0.04, -0.21, 0.56, 0),
    box(0.045, 1.12, 0.04, 0.21, 0.56, 0),
    box(0.44, 0.022, 0.26, 0, 0.02, -0.14),
    box(0.42, 0.035, 0.035, 0, 0.36, 0),
    box(0.42, 0.035, 0.035, 0, 0.76, 0),
    box(0.5, 0.04, 0.04, 0, 1.1, 0),
  ];
  const wheels = () => [rodX(0.115, 0.05, 8, -0.25, 0.115, 0.06), rodX(0.115, 0.05, 8, 0.25, 0.115, 0.06)];

  const leanF = merge(frame());
  const leanW = merge(wheels());
  tumble([leanF, leanW], 0.3, 0.03);
  settle([leanF, leanW]);

  const downF = merge(frame());
  const downW = merge(wheels());
  tumble([downF, downW], 1.44, 0.06, 0.2);
  settle([downF, downW]);

  const loadedF = merge(frame());
  const loadedW = merge(wheels());
  const load = merge([ybox(0.42, 0.34, 0.34, 0, 0.21, -0.2, 0.06)]);
  tumble([loadedF, loadedW, load], 0.3, 0.03);
  settle([loadedF, loadedW, load]);

  return makeShape(spec, { kind: 'floor', yawJitter: 0.5 }, [
    layer(matte(pal.metal), [leanF, downF, loadedF]),
    layer(matte(pal.dark), [leanW, downW, loadedW]),
    layer(matte(pal.cardboard), [null, null, load]),
  ]);
}

/* ================================================================== *
 * Pipes
 * ================================================================== */

/**
 * Pipes running along a wall. The run is a little longer than a cell so that
 * two adjacent spots read as one continuous service line rather than two
 * unrelated props.
 */
function buildPipeRun(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  // Just longer than a cell, so two adjacent spots read as one continuous
  // service line instead of two unrelated props — but not so much longer that a
  // run at the end of a wall hangs out into the room.
  const L = spec.cellSize + 0.06;
  const half = L / 2;
  const pipe = (r: number, y: number, z: number) => strut(r, -half, y, z, half, y, z, 8);
  const bracket = (x: number) => [box(0.03, 0.42, 0.03, x, 0.01, -0.05), box(0.03, 0.03, 0.14, x, 0.2, -0.09)];

  const three = merge([pipe(0.075, 0, -0.1), pipe(0.05, 0.16, -0.085), pipe(0.032, -0.14, -0.07)]);
  const threeBrackets = merge([...bracket(-half * 0.6), ...bracket(half * 0.6)]);

  const lagged = merge([pipe(0.075, 0, -0.1), pipe(0.032, -0.14, -0.07)]);
  const laggedBrackets = merge([...bracket(-half * 0.6), ...bracket(half * 0.6)]);
  const lagging = strut(0.105, -half * 0.45, 0, -0.1, half * 0.35, 0, -0.1, 8);

  const single = merge([
    pipe(0.1, 0, -0.13),
    // A bolted flange where two lengths were joined, roughly mid-run.
    rodX(0.15, 0.03, 8, -0.08, 0, -0.13),
    rodX(0.15, 0.03, 8, 0.08, 0, -0.13),
  ]);
  const singleBrackets = merge([...bracket(-half * 0.7), ...bracket(half * 0.7)]);

  return makeShape(spec, { kind: 'wall', height: Math.max(1.6, spec.ceilingHeight - 0.45) }, [
    layer(matte(pal.metal), [three, lagged, single]),
    layer(matte(pal.metalDark), [threeBrackets, laggedBrackets, singleBrackets]),
    layer(matte(pal.pale), [null, lagging, null]),
  ]);
}

/** A wheel valve on a stub of pipe. Never turned. */
function buildValve(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const stub = () => [rodZ(0.055, 0.18, 8, 0, 0, -0.09), rodZ(0.1, 0.02, 8, 0, 0, -0.02), box(0.13, 0.15, 0.13, 0, 0, -0.2)];

  const wheelGeo = new THREE.TorusGeometry(0.135, 0.017, 3, 10);
  wheelGeo.translate(0, 0, -0.3);
  const spokes: THREE.BufferGeometry[] = [];
  for (let i = 0; i < 3; i++) {
    const g = box(0.25, 0.018, 0.018);
    g.rotateZ((i * Math.PI * 2) / 3);
    g.translate(0, 0, -0.3);
    spokes.push(g);
  }
  const wheel = merge([wheelGeo, ...spokes, rodZ(0.03, 0.06, 6, 0, 0, -0.3)]);

  const lever = merge([
    (() => {
      const g = box(0.3, 0.035, 0.035);
      g.rotateZ(0.62);
      g.translate(0.09, 0.09, -0.28);
      return g;
    })(),
    rodZ(0.03, 0.07, 6, 0, 0, -0.29),
  ]);

  const gaugeBody = merge([rodZ(0.075, 0.04, 10, 0, 0, -0.25), rodZ(0.02, 0.09, 6, 0, -0.06, -0.21)]);
  const gaugeFace = rodZ(0.06, 0.01, 10, 0, 0, -0.276);

  return makeShape(spec, { kind: 'wall', height: 1.15 }, [
    layer(matte(pal.metal), [merge(stub()), merge(stub()), merge(stub())]),
    layer(matte(pal.metalDark), [wheel, lever, gaugeBody]),
    layer(matte(pal.pale), [null, null, gaugeFace]),
  ]);
}

/** A junction box with conduit going somewhere you cannot follow. */
function buildJunctionBox(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const body = () => [box(0.26, 0.34, 0.11, 0, 0, -0.055), box(0.28, 0.36, 0.02, 0, 0, -0.115)];
  const conduitUp = () => strut(0.022, 0, 0.17, -0.05, 0, 0.72, -0.05, 6);
  const conduitDown = () => strut(0.022, 0, -0.17, -0.05, 0, -0.6, -0.05, 6);

  const closed = merge([...body(), conduitUp()]);
  const closedDetail = merge([box(0.03, 0.05, 0.02, -0.15, 0, -0.12), box(0.03, 0.05, 0.02, 0.15, 0, -0.12)]);

  // Open: the lid swung aside on its hinge, and the dark inside it.
  const lid = box(0.26, 0.34, 0.02, 0, 0, -0.01);
  lid.rotateY(-1.15);
  lid.translate(-0.14, 0, -0.11);
  const open = merge([box(0.26, 0.34, 0.11, 0, 0, -0.055), lid, conduitUp()]);
  const openDetail = merge([box(0.22, 0.3, 0.02, 0, 0, -0.1), box(0.03, 0.05, 0.02, 0.15, 0, -0.12)]);

  const both = merge([...body(), conduitUp(), conduitDown()]);
  const bothDetail = merge([box(0.03, 0.05, 0.02, -0.15, 0, -0.12), box(0.03, 0.05, 0.02, 0.15, 0, -0.12)]);

  return makeShape(spec, { kind: 'wall', height: 1.5 }, [
    layer(matte(pal.metal), [closed, open, both]),
    layer(matte(pal.dark), [closedDetail, openDetail, bothDetail]),
  ]);
}

/** A fixed wall ladder. It goes up. Whether there is anything up there is not this file's problem. */
function buildLadder(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const H = Math.min(spec.ceilingHeight - 0.15, 3.0);
  const rails = () => [
    box(0.04, H, 0.032, -0.21, H / 2, -0.085),
    box(0.04, H, 0.032, 0.21, H / 2, -0.085),
    box(0.04, 0.05, 0.085, -0.21, 0.4, -0.042),
    box(0.04, 0.05, 0.085, 0.21, 0.4, -0.042),
    box(0.04, 0.05, 0.085, -0.21, H - 0.35, -0.042),
    box(0.04, 0.05, 0.085, 0.21, H - 0.35, -0.042),
  ];
  const rungs = (skip: readonly number[]) => {
    const parts: THREE.BufferGeometry[] = [];
    const n = Math.max(3, Math.floor((H - 0.45) / 0.3));
    for (let i = 0; i < n; i++) {
      if (skip.includes(i)) continue;
      parts.push(box(0.42, 0.028, 0.028, 0, 0.35 + i * 0.3, -0.085));
    }
    return parts;
  };

  const full = merge([...rails(), ...rungs([])]);
  const gapped = merge([...rails(), ...rungs([2, 5])]);
  const hatch = merge([
    ...rails(),
    ...rungs([]),
    box(0.62, 0.05, 0.05, 0, H - 0.02, -0.24),
    box(0.05, 0.05, 0.34, -0.29, H - 0.02, -0.24),
    box(0.05, 0.05, 0.34, 0.29, H - 0.02, -0.24),
  ]);

  return makeShape(spec, { kind: 'wall', grounded: true }, solo(matte(pal.metalDark), [full, gapped, hatch]));
}

/** Standing water on the floor. A darker patch with a lip that catches a torch. */
function buildPuddle(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const texture = makePuddleTexture(css(pal.glass), css(pal.pale), 0x71b3d5);
  const sizes: ReadonlyArray<readonly [number, number]> = [
    [1.5, 1.2],
    [2.3, 1.1],
    [0.95, 0.85],
  ];
  const variants = sizes.map(([w, d]) => {
    const g = new THREE.PlaneGeometry(w, d);
    g.rotateX(-Math.PI / 2);
    return g;
  });

  // The one place a specular response is worth a second shader: wet is a
  // highlight, and a Lambert puddle just reads as a dark rug.
  const material = new THREE.MeshStandardMaterial({
    map: texture,
    color: 0xffffff,
    roughness: 0.14,
    metalness: 0,
    transparent: true,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
  });

  return makeShape(spec, { kind: 'floor', out: 0.012, yawJitter: Math.PI }, solo(material, variants), [texture]);
}

/* ================================================================== *
 * Poolrooms
 * ================================================================== */

/** A stainless pool ladder, rails curving over an edge that may not be there. */
function buildPoolLadder(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const rail = (x: number, top: number) => [
    strut(0.022, x, 0.02, 0, x, top, 0, 6),
    strut(0.022, x, top, 0, x, top + 0.12, -0.14, 6),
    strut(0.022, x, top + 0.12, -0.14, x, top - 0.06, -0.4, 6),
    cyl(0.055, 0.06, 0.03, 8, x, 0.015, 0),
  ];
  const rung = (y: number) => strut(0.02, -0.22, y, 0.02, 0.22, y, 0.02, 6);

  const low = merge([...rail(-0.22, 0.82), ...rail(0.22, 0.82), rung(0.26), rung(0.56)]);
  const tall = merge([...rail(-0.22, 1.02), ...rail(0.22, 1.02), rung(0.24), rung(0.52), rung(0.8)]);

  return makeShape(spec, { kind: 'floor', yawJitter: 0.1 }, solo(matte(pal.chrome, 0.1), [low, tall]));
}

/** A tiled bench. Somewhere to sit that nobody is sitting on. */
function buildBench(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const seat = () => box(1.5, 0.08, 0.42, 0, 0.44, -0.21);
  const legs = () => [box(0.1, 0.44, 0.34, -0.58, 0.22, -0.21), box(0.1, 0.44, 0.34, 0.58, 0.22, -0.21)];

  const plainSeat = merge([seat()]);
  const plainLegs = merge(legs());

  const backedSeat = merge([seat(), box(1.5, 0.28, 0.06, 0, 0.78, -0.03)]);
  const backedLegs = merge([...legs(), box(0.08, 0.5, 0.08, -0.58, 0.66, -0.05), box(0.08, 0.5, 0.08, 0.58, 0.66, -0.05)]);

  const towelSeat = merge([seat()]);
  const towelLegs = merge(legs());
  const towel = merge([
    ybox(0.42, 0.035, 0.36, 0.34, 0.5, -0.2, 0.1),
    ybox(0.4, 0.24, 0.03, 0.34, 0.36, -0.39, 0.06),
  ]);

  // Wall-backed and wide, so the wobble stays tiny — see `buildShelving`.
  return makeShape(spec, { kind: 'floor', yawJitter: 0.035 }, [
    layer(matte(pal.pale), [plainSeat, backedSeat, towelSeat]),
    layer(matte(pal.plasticDark), [plainLegs, backedLegs, towelLegs]),
    layer(matte(pal.fabric), [null, null, towel]),
  ]);
}

/** An inflatable ring. Barely tinted: nobody down here made this. */
function buildFloatRing(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const ring = () => new THREE.TorusGeometry(0.34, 0.1, 4, 10);

  const flat = ring();
  flat.rotateX(-Math.PI / 2);
  flat.translate(0, 0.1, 0);

  const upright = ring();
  upright.rotateX(-0.2);
  settle([upright]);

  const soft = ring();
  soft.rotateX(-Math.PI / 2);
  soft.scale(1, 0.55, 1.05);
  settle([soft]);

  return makeShape(spec, { kind: 'floor', yawJitter: Math.PI, tilt: 0.05 }, solo(matte(pal.hazard), [flat, upright, soft]));
}

/** A floor drain. The only place in the level that goes anywhere. */
function buildDrain(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const bars = (span: number) => {
    const parts: THREE.BufferGeometry[] = [];
    for (let i = 0; i < 5; i++) {
      parts.push(box(span, 0.014, 0.022, 0, 0.019, -0.08 + i * 0.04));
    }
    return parts;
  };

  const roundRim = merge([cyl(0.17, 0.17, 0.02, 12, 0, 0.01, 0), ...bars(0.3)]);
  const squareRim = merge([box(0.34, 0.02, 0.34, 0, 0.01, 0), ...bars(0.3)]);
  const goneRim = merge([cyl(0.19, 0.19, 0.02, 12, 0, 0.01, 0)]);

  const roundVoid = cyl(0.14, 0.14, 0.012, 12, 0, 0.004, 0);
  const squareVoid = box(0.28, 0.012, 0.28, 0, 0.004, 0);
  const goneVoid = cyl(0.16, 0.16, 0.012, 12, 0, 0.004, 0);

  return makeShape(spec, { kind: 'floor', out: 0.004, yawJitter: Math.PI }, [
    layer(matte(pal.metalDark), [roundRim, squareRim, goneRim]),
    layer(matte(pal.dark, 0.01), [roundVoid, squareVoid, goneVoid]),
  ]);
}

/** A dropped towel. Three slabs and a fold is enough for cloth at this range. */
function buildTowel(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const heap = merge([
    ybox(0.5, 0.035, 0.34, 0, 0.017, 0, 0.2),
    ybox(0.42, 0.04, 0.27, 0.03, 0.05, -0.02, -0.5),
    ybox(0.3, 0.035, 0.2, -0.02, 0.085, 0.03, 0.9),
    ybox(0.22, 0.018, 0.13, 0.3, 0.012, 0.16, 0.4),
  ]);
  const spread = merge([ybox(0.62, 0.02, 0.4, 0, 0.01, 0, 0.05), ybox(0.3, 0.018, 0.36, 0.1, 0.026, 0.02, 0.14)]);
  const folded = merge([ybox(0.36, 0.05, 0.26, 0, 0.025, 0, 0), ybox(0.34, 0.045, 0.24, 0.01, 0.07, 0.01, 0.06)]);

  return makeShape(spec, { kind: 'floor', yawJitter: Math.PI }, solo(matte(pal.fabric), [heap, spread, folded]));
}

/**
 * One shoe, still laced, in the middle of a corridor.
 *
 * The smallest prop in the game and the only one that is a *person*. Everything
 * else somebody left behind is equipment — a camp, a torch, writing on a wall —
 * and reads as somebody who was managing. A single laced shoe reads as somebody
 * who was not, and it does it without a drop of blood, which is the register
 * this game keeps.
 *
 * Laced is the whole thing and it is why the laces are modelled rather than
 * painted: a shoe kicked off is a shoe somebody took off. Three variants, all
 * of them a left shoe, because a pair is luggage.
 */
function buildShoe(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  // Not `fabric` — this is leather, and it wants to be a shade darker and
  // browner than the level's soft furnishings so it does not read as a rag.
  const upper = matte(belong(0x3a2c22, pal));
  const sole = matte(belong(0x2a2622, pal));
  const lace = matte(belong(0xbeb6a4, pal, pal.pull * 0.6));

  /** Sole, heel and upper for one shoe, tipped `roll` radians onto its side. */
  const body = (roll: number, yaw: number): THREE.BufferGeometry => {
    const g = merge([
      // Sole: three tapering plates rather than one box, so the toe is narrower
      // than the waist and the shoe has a direction from across the room.
      ybox(0.098, 0.022, 0.11, 0, 0.011, -0.075),
      ybox(0.104, 0.026, 0.13, 0, 0.013, 0.015),
      ybox(0.088, 0.038, 0.075, 0, 0.019, 0.105),
      // Upper: toe box, vamp, and the collar standing above the heel.
      ybox(0.09, 0.052, 0.1, 0, 0.048, -0.072),
      ybox(0.094, 0.062, 0.09, 0, 0.055, 0.012),
      ybox(0.086, 0.078, 0.07, 0, 0.07, 0.088),
    ]);
    g.rotateZ(roll);
    g.rotateY(yaw);
    return g;
  };

  /** The lacing: four crossed bars over the throat, and two loose ends. */
  const lacing = (roll: number, yaw: number, slack: number): THREE.BufferGeometry => {
    const bars: THREE.BufferGeometry[] = [];
    for (let i = 0; i < 4; i++) {
      const z = -0.03 + i * 0.032;
      const y = 0.082 + i * 0.004;
      bars.push(ybox(0.082, 0.007, 0.008, 0, y, z, 0.42));
      bars.push(ybox(0.082, 0.007, 0.008, 0, y, z, -0.42));
    }
    // The ends, lying wherever they fell. This is the only part of the prop
    // that varies, and it is what stops three shoes reading as one shoe.
    bars.push(ybox(0.008, 0.006, 0.12, -0.035, 0.006, -0.11, slack));
    bars.push(ybox(0.008, 0.006, 0.09, 0.03, 0.006, -0.125, -slack * 1.4));
    const g = merge(bars);
    g.rotateZ(roll);
    g.rotateY(yaw);
    return g;
  };

  // Upright and squared to the corridor is the one arrangement that reads as
  // placed rather than dropped, so no variant is.
  const poses: Array<[number, number, number]> = [
    [0.14, 0.5, 0.35],
    [1.05, -0.9, -0.6],
    [-0.28, 2.4, 0.9],
  ];

  return makeShape(spec, { kind: 'floor', yawJitter: Math.PI }, [
    layer(
      upper,
      poses.map(([roll, yaw]) => body(roll, yaw))
    ),
    layer(
      sole,
      poses.map(([roll, yaw]) => ybox(0.108, 0.008, 0.28, 0, 0.004, 0.01, 0).rotateZ(roll).rotateY(yaw))
    ),
    layer(
      lace,
      poses.map(([roll, yaw, slack]) => lacing(roll, yaw, slack))
    ),
  ]);
}

/* ================================================================== *
 * Left by people
 * ================================================================== */

/** Marker on wallpaper. Eight phrases, one texture, one material. */
function buildGraffiti(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const { ink } = inkColours(pal);
  const atlas: DecalAtlas = makeGraffitiAtlas(ink);
  const rng = makeRNG(0x1f4d);
  const variants = atlas.entries.map((entry) => wallDecal(entry, 0.26, -0.014, rng.range(-0.05, 0.05)));
  return makeShape(spec, { kind: 'wall', height: 1.45 }, solo(inkMaterial(atlas.texture), variants), [atlas]);
}

/** Days, in fives, scratched into the wall by a hand that got worse at it. */
function buildTallyMarks(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const { ink, lip } = inkColours(pal);
  const atlas = makeTallyAtlas(ink, lip);
  const rng = makeRNG(0x77c1);
  // Same reasoning as the arrow: at 0.3 m a forty-four mark count came out as a
  // 3 cm-per-stroke barcode that was a smudge by three metres, which loses the
  // one thing the prop is for — that somebody sat here long enough to lose
  // count. The rows carry their own widths, so the short counts stay small and
  // only the long vigils become a band across the wall.
  const variants = atlas.entries.map((entry) => wallDecal(entry, 0.44, -0.014, rng.range(-0.035, 0.035)));
  return makeShape(spec, { kind: 'wall', height: 1.3 }, solo(inkMaterial(atlas.texture), variants), [atlas]);
}

/**
 * Scratched directions, and the one prop whose orientation carries meaning.
 *
 * `PropSpot.variant & 1` is the side the arrow points, and the atlas rows are
 * ordered so that `variantIndex`'s `variant % rows` lands that bit on the row's
 * own low bit (see `ARROW_FORM_ANGLES`). The roll is kept small for the same
 * reason: a hand-drawn lean is character, a big one is a different direction.
 */
function buildArrow(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const { ink, lip } = inkColours(pal);
  const atlas = makeArrowAtlas(ink, lip);
  const rng = makeRNG(0x2ea9);
  // Taller than the writing decals on purpose. Graffiti is a sentence and reads
  // as *presence* from across a room even when the words do not; an arrow is a
  // single instruction that is worthless unless the direction survives the
  // distance the room is wide. At 0.46 it was a smudge by eight metres.
  const variants = atlas.entries.map((entry) => wallDecal(entry, 0.62, -0.014, rng.range(-0.08, 0.08)));
  return makeShape(spec, { kind: 'wall', height: 1.55 }, solo(inkMaterial(atlas.texture), variants), [atlas]);
}

/**
 * The building's own signage: printed, screwed on, and no longer true.
 *
 * Two changes bought by the slides, because signs are now the thing that points
 * at the way down and a sign that cannot be read is not pointing at anything.
 *
 *  - **Bigger.** At 0.2 m the plate was a postage stamp on a wall four metres
 *    wide: legible at three metres, invisible at ten, and read as a switch plate
 *    at anything past that. 0.32 m is the size the real ones are, which is the
 *    size that was arrived at by people solving this exact problem.
 *  - **Lit.** Row 0 is EXIT (see `SIGN_ROW`), and a real exit sign is
 *    illuminated — it is the one fitting in a building specified to be visible
 *    with the power off. It gets its own emissive material and every other row
 *    keeps the plain one, so the green plate glows in an unlit room and the
 *    B-14 next to it does not.
 */
function buildSign(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const atlas = makeSignAtlas((hex) => css(belong(hex, pal, pal.pull * 0.55, pal.gain)));
  const HEIGHT = 0.32;

  const face = (entry: AtlasEntry): THREE.BufferGeometry => wallDecal(entry, HEIGHT, -0.026);
  const plates = atlas.entries.map((entry) => box(HEIGHT * entry.aspect + 0.018, HEIGHT + 0.018, 0.025, 0, 0, -0.0125));

  // One mesh per material either way, so splitting the rows costs a draw call on
  // a prop there are one to three of, and nothing at all in geometry.
  const dark = atlas.entries.map((entry, i) => (i === SIGN_ROW.exit ? null : face(entry)));
  const lit = atlas.entries.map((entry, i) => (i === SIGN_ROW.exit ? face(entry) : null));

  return makeShape(
    spec,
    { kind: 'wall', height: 1.95, yawJitter: 0.02 },
    [
      layer(matte(pal.metalDark), plates),
      layer(new THREE.MeshLambertMaterial({ map: atlas.texture }), dark),
      layer(
        new THREE.MeshLambertMaterial({
          map: atlas.texture,
          emissive: 0xffffff,
          emissiveMap: atlas.texture,
          emissiveIntensity: 0.62,
        }),
        lit
      ),
    ],
    [atlas]
  );
}

/**
 * Somebody stopped here and did not leave.
 *
 * A bag, a dead torch, the cartons they had left, and paper. The three variants
 * are three stages of the same story: laid out and lived in, packed up in a
 * hurry, and — the last one — the bedding still here with everything else gone.
 * Nothing about it is dressed up; it just has to read instantly and be sad.
 */
function buildCamp(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);

  const sleepingBag = (): THREE.BufferGeometry[] => {
    const bag = new THREE.SphereGeometry(0.3, 8, 5);
    bag.scale(1.0, 0.42, 2.3);
    bag.translate(0, 0.126, 0.1);
    // The open end, turned back on itself.
    const flap = new THREE.SphereGeometry(0.27, 7, 4);
    flap.scale(1.0, 0.3, 0.66);
    flap.translate(0.03, 0.09, -0.66);
    return [bag, flap];
  };

  const laidOut = merge([...sleepingBag(), ybox(0.36, 0.09, 0.3, 0.54, 0.045, 0.38, 0.42)]);

  const rolled = (() => {
    const roll = new THREE.CylinderGeometry(0.17, 0.17, 0.78, 8, 1);
    roll.rotateZ(Math.PI / 2);
    roll.translate(0, 0.17, 0);
    return merge([roll, box(0.04, 0.35, 0.35, -0.2, 0.175, 0), box(0.04, 0.35, 0.35, 0.2, 0.175, 0)]);
  })();

  const abandoned = merge([
    ybox(0.74, 0.035, 1.15, 0, 0.017, 0, 0.05),
    ybox(0.4, 0.05, 0.34, 0.1, 0.05, -0.42, 0.6),
  ]);

  // Dead, and lying where it was put down. The two variants that still have gear
  // share the one buffer.
  const torch = merge([rodX(0.032, 0.17, 6, 0.42, 0.034, -0.55), rodX(0.042, 0.05, 6, 0.53, 0.036, -0.55)]);

  const cartonsFull = merge([
    box(0.07, 0.19, 0.07, -0.42, 0.095, -0.5),
    (() => {
      const g = box(0.07, 0.19, 0.07);
      g.rotateZ(Math.PI / 2);
      g.rotateY(0.4);
      g.translate(-0.3, 0.035, -0.68);
      return g;
    })(),
    (() => {
      const g = box(0.07, 0.19, 0.07);
      g.rotateX(Math.PI / 2);
      g.rotateY(-0.3);
      g.translate(-0.56, 0.035, -0.3);
      return g;
    })(),
    box(0.08, 0.06, 0.075, -0.2, 0.03, -0.36),
  ]);

  const cartonsSpilled = merge([
    (() => {
      const g = box(0.07, 0.19, 0.07);
      g.rotateZ(Math.PI / 2);
      g.rotateY(0.9);
      g.translate(0.36, 0.035, -0.28);
      return g;
    })(),
    (() => {
      const g = box(0.07, 0.19, 0.07);
      g.rotateZ(Math.PI / 2);
      g.rotateY(-0.2);
      g.translate(0.5, 0.035, -0.05);
      return g;
    })(),
    box(0.08, 0.055, 0.075, 0.28, 0.028, 0.2),
  ]);

  const cartonLast = merge([box(0.07, 0.19, 0.07, 0.36, 0.095, -0.5)]);

  const paper = (seed: number, n: number) => {
    const rng = makeRNG(seed);
    const parts: THREE.BufferGeometry[] = [];
    for (let i = 0; i < n; i++) {
      parts.push(
        slab(0.2, 0.28, rng.range(-0.55, 0.55), 0.005 + i * 0.0015, rng.range(-0.75, 0.6), rng.range(0, Math.PI * 2))
      );
    }
    return merge(parts);
  };

  return makeShape(spec, { kind: 'floor', yawJitter: Math.PI }, [
    layer(matte(pal.blanket), [laidOut, rolled, abandoned]),
    layer(matte(pal.dark), [torch, torch, null]),
    layer(matte(pal.carton), [cartonsFull, cartonsSpilled, cartonLast]),
    layer(matte(pal.paper, 0.09), [paper(0x9a31, 3), paper(0x4b77, 2), paper(0xd105, 4)]),
  ]);
}

/* ================================================================== *
 * Architecture that does not make sense
 *
 * Not props shaped like walls — pieces of the same building, put where nothing
 * needs them and then left. `propwalls.ts` hands out the level's own wall
 * surface, its skirting board and the plain core that shows wherever the
 * building has been cut, so a half-wall is the room's wallpaper on both faces
 * with the room's skirting along its foot, and then an end that simply stops.
 * The stop is the whole effect: not a strange object, an ordinary one nobody
 * finished.
 *
 * Every kind here is centred on its local origin in X and Z, because its
 * collision box in `PROP_DEFS` is. A wall drawn off the box that blocks it is an
 * invisible wall on one side and a ghost on the other.
 * ================================================================== */

/** Fold a wall's three materials' worth of geometry into finished layers. */
function wallLayers(arch: ArchKit, variants: readonly WallParts[]): PropLayer[] {
  return [
    layer(
      arch.paper,
      variants.map((v) => (v.paper.length ? mergeUV(v.paper) : null))
    ),
    layer(
      arch.skirt,
      variants.map((v) => (v.skirt.length ? mergeUV(v.skirt) : null))
    ),
    layer(
      arch.plain,
      variants.map((v) => (v.plain.length ? merge(v.plain) : null))
    ),
  ];
}

/** Shift a whole wall assembly. Columns are authored at the origin and moved. */
function moveParts(p: WallParts, dx: number, dy: number, dz: number): WallParts {
  for (const g of p.paper) g.translate(dx, dy, dz);
  for (const g of p.skirt) g.translate(dx, dy, dz);
  for (const g of p.plain) g.translate(dx, dy, dz);
  return p;
}

/** Turn a geometry about a point in its own XY plane. Doors swing, columns lean. */
function pivot(g: THREE.BufferGeometry, px: number, py: number, angle: number): THREE.BufferGeometry {
  g.translate(-px, -py, 0);
  g.rotateZ(angle);
  g.translate(px, py, 0);
  return g;
}

/**
 * A wall that stops at waist height, part of the way across a room.
 *
 * Papered on both faces and skirted along its foot, so it is unmistakably the
 * same building — and then capped and cut off in mid-air. It divides nothing
 * from nothing, and the ends are what say so.
 */
function buildHalfWall(spec: LevelSpec): PropShape {
  const arch = acquireArch(spec);
  const L = 3.0;
  const H = 1.05;
  // 0.26 of core plus the skirting standing 0.03 proud of each face is the 0.32
  // the footprint blocks.
  const CORE = 0.26;

  const bare = buildingWall(L, H, CORE, { capped: true });

  const railed = buildingWall(L, H - 0.045, CORE, { capped: true, uOffset: 0.9 });
  // A capping rail, the way a real dwarf wall is finished off.
  railed.plain.push(box(L + 0.05, 0.045, CORE + 0.09, 0, H - 0.0225, 0));

  const unfinished = buildingWall(L, H, CORE, {
    capped: true,
    paperEnds: L * 0.62,
    skirtEnds: L * 0.79,
    uOffset: 1.7,
  });

  return makeShape(spec, { kind: 'floor', yawJitter: 0.012 }, wallLayers(arch, [bare, railed, unfinished]), [
    releaseArch(spec),
  ]);
}

/**
 * A full-height wall that simply stops, ending in mid-air.
 *
 * `anchorProp` caps a floor prop's scale so its top lands 4 cm under the
 * ceiling, so this can never quite meet it. That is left alone rather than
 * worked around: cheap partitions really do stop short of the slab, and the
 * thing that unsettles is the vertical cut in open floor, not the head detail.
 */
function buildStubWall(spec: LevelSpec): PropShape {
  const arch = acquireArch(spec);
  const L = 2.4;
  const H = spec.ceilingHeight;
  const CORE = 0.3;
  const f = CORE / 2 + 0.007;

  const plain = buildingWall(L, H, CORE, {});
  const stopped = buildingWall(L, H, CORE, { paperEnds: L - 0.72, uOffset: 1.3 });
  const naked = buildingWall(L, H, CORE, { bareFace: -1, skirtEnds: L - 0.5, uOffset: 2.4 });

  // A switch and a socket, wired to a wall with nothing on either side of it.
  const fittings: Array<THREE.BufferGeometry | null> = [
    null,
    merge([box(0.086, 0.086, 0.014, -0.52, 1.24, -f)]),
    merge([box(0.086, 0.086, 0.014, 0.44, 1.24, f), box(0.14, 0.086, 0.014, -0.1, 0.3, f)]),
  ];

  return makeShape(
    spec,
    { kind: 'floor', yawJitter: 0.01 },
    [...wallLayers(arch, [plain, stopped, naked]), layer(arch.joinery, fittings)],
    [releaseArch(spec)]
  );
}

/**
 * A doorframe standing on its own, leading from nothing to nothing.
 *
 * The opening is genuinely open in all three variants, and that is the contract
 * rather than a stylistic choice: this kind carries **no footprint**, because a
 * single box across the opening would be an invisible wall inside a visible
 * doorway and because the placer is allowed to stand one across a one-cell
 * corridor. Anything drawn across the gap — a shut leaf, boards — would be
 * something the player walks straight through. What varies instead is what
 * became of the door: taken off, fallen flat through the frame, wrenched out.
 * The fallen leaf lies 4 cm proud of the floor, well under `stepOverHeight`, so
 * walking over it is honest too.
 */
function buildFreeDoorframe(spec: LevelSpec): PropShape {
  const arch = acquireArch(spec);
  const pal = propPalette(spec);
  const OPEN_W = 0.9;
  const OPEN_H = 1.94;
  const LINING = 0.045;
  const DEPTH = 0.14;
  const CASE_W = 0.115;
  const CASE_P = 0.02;

  const jx = OPEN_W / 2 + LINING / 2;
  const cx = OPEN_W / 2 + LINING + CASE_W / 2;
  const cz = DEPTH / 2 + CASE_P / 2;
  const head = OPEN_H + LINING;

  const frame = (): THREE.BufferGeometry[] => {
    const parts = [
      box(LINING, OPEN_H, DEPTH, -jx, OPEN_H / 2, 0),
      box(LINING, OPEN_H, DEPTH, jx, OPEN_H / 2, 0),
      box(OPEN_W + LINING * 2, LINING, DEPTH, 0, head - LINING / 2, 0),
    ];
    for (const s of [1, -1] as const) {
      parts.push(
        box(CASE_W, head + CASE_W, CASE_P, -cx, (head + CASE_W) / 2, s * cz),
        box(CASE_W, head + CASE_W, CASE_P, cx, (head + CASE_W) / 2, s * cz),
        box(OPEN_W + (LINING + CASE_W) * 2, CASE_W, CASE_P, 0, head + CASE_W / 2, s * cz)
      );
    }
    return parts;
  };

  /** The leaf, off its hinges and lying flat through the opening. */
  const fallenLeaf = (yaw: number, dx: number, dz: number): THREE.BufferGeometry =>
    ybox(OPEN_W - 0.014, 0.042, OPEN_H - 0.014, dx, 0.021, dz, yaw);

  const hinges = (): THREE.BufferGeometry[] =>
    [0.24, 0.98, OPEN_H - 0.14].map((y) => box(0.02, 0.09, 0.055, -jx, y, -0.03));

  // 1. The door was taken off and carried away. Nothing but the frame.
  const empty = merge(frame());
  // 2. It came off and is lying through its own opening, face up.
  const fallen = merge([...frame(), fallenLeaf(0.05, 0.02, 0.06)]);
  // 3. Wrenched out: the lining split, the leaf askew on the floor.
  const wrenched = merge([
    ...frame().slice(1),
    // Leant off plumb about its own foot, so the stub still meets the floor.
    pivot(box(LINING, OPEN_H * 0.62, DEPTH, -jx, (OPEN_H * 0.62) / 2, 0), -jx, 0, 0.035),
    // Kept inside its own cell: Pipe Dreams' cells are 2.6 m, and a leaf that
    // reached past 1.3 m from the frame would lie through the corridor wall.
    fallenLeaf(0.33, -0.14, 0.08),
  ]);

  const dark = [
    // The strike plate on one jamb, the hinges on the other. All that is left.
    merge([...hinges(), box(0.04, 0.16, 0.012, jx, 1.0, -0.075)]),
    merge([...hinges(), box(0.04, 0.16, 0.012, jx, 1.0, -0.075)]),
    // Torn out with the lining: two of the three hinge plates and the screws.
    merge([
      box(0.02, 0.09, 0.055, -jx, 0.24, -0.03),
      box(0.02, 0.09, 0.055, -jx, 0.98, -0.03),
      box(0.05, 0.02, 0.09, -jx - 0.06, 0.01, 0.2),
    ]),
  ];

  /** The handle, on the floor with the leaf that carries it. */
  const flatHandle = (yaw: number, dx: number, dz: number): THREE.BufferGeometry =>
    merge([
      ybox(0.026, 0.05, 0.026, dx + Math.cos(yaw) * 0.3, 0.067, dz - Math.sin(yaw) * 0.3, yaw),
      ybox(0.11, 0.024, 0.024, dx + Math.cos(yaw) * 0.25, 0.095, dz - Math.sin(yaw) * 0.25, yaw),
    ]);

  return makeShape(
    spec,
    { kind: 'floor', yawJitter: 0.01 },
    [
      layer(arch.joinery, [empty, fallen, wrenched]),
      layer(arch.joineryDark, dark),
      layer(matte(pal.chrome, 0.08), [null, flatHandle(0.05, 0.02, 0.06), flatHandle(0.33, -0.14, 0.24)]),
    ],
    [releaseArch(spec)]
  );
}

/**
 * A door set in a wall that has never opened.
 *
 * Mounted flush: `Anchor.out` cancels the set-back the placer gives wall props,
 * so the casing lands on the wallpaper instead of hovering 8 cm off it.
 */
function buildFalseDoor(spec: LevelSpec): PropShape {
  const arch = acquireArch(spec);
  const pal = propPalette(spec);
  const W = 0.9;
  const H = 2.02;
  const CASE_W = 0.115;
  const CASE_P = 0.022;
  const LEAF = 0.042;

  const casing = (): THREE.BufferGeometry[] => [
    box(CASE_W, H + CASE_W, CASE_P, -(W / 2 + CASE_W / 2), (H + CASE_W) / 2, -CASE_P / 2),
    box(CASE_W, H + CASE_W, CASE_P, W / 2 + CASE_W / 2, (H + CASE_W) / 2, -CASE_P / 2),
    box(W + CASE_W * 2, CASE_W, CASE_P, 0, H + CASE_W / 2, -CASE_P / 2),
  ];
  const leaf = (): THREE.BufferGeometry => box(W - 0.012, H - 0.012, LEAF, 0, (H - 0.012) / 2 + 0.006, -CASE_P - LEAF / 2);
  const z = -CASE_P - LEAF - 0.004;

  const flush = merge([...casing(), leaf()]);
  const glazed = merge([...casing(), leaf()]);
  const painted = merge([...casing(), leaf()]);

  const hardware = merge([
    box(0.028, 0.028, 0.05, 0.3, 1.02, z + 0.02),
    box(0.115, 0.026, 0.026, 0.25, 1.02, z - 0.012),
    box(0.036, 0.09, 0.008, 0.3, 0.87, z),
  ]);
  // Nothing left but the spindle hole and the plate somebody kicked for years.
  const stripped = merge([box(0.03, 0.03, 0.01, 0.3, 1.02, z), box(W - 0.08, 0.24, 0.008, 0, 0.16, z)]);

  const visionSurround = merge([
    box(0.28, 0.78, 0.012, 0, 1.46, z),
    box(0.115, 0.026, 0.026, 0.25, 1.02, z - 0.012),
    box(0.028, 0.028, 0.05, 0.3, 1.02, z + 0.02),
  ]);
  const vision = box(0.2, 0.7, 0.008, 0, 1.46, z - 0.008);

  return makeShape(
    spec,
    // Flush to the wall, standing on the floor: `height` is 0 in `PROP_DEFS`.
    { kind: 'wall', height: 0, out: WALL_INSET },
    [
      layer(arch.joinery, [flush, glazed, painted]),
      layer(arch.joineryDark, [hardware, visionSurround, stripped]),
      layer(matte(pal.glass, 0.02), [null, vision, null]),
    ],
    [releaseArch(spec)]
  );
}

/**
 * A bulkhead crossing the room, boxing in a service that is not there.
 *
 * Hangs from the ceiling, so nothing about it obstructs the player and it
 * carries no footprint. It works on the ceiling line rather than the floor: a
 * room whose ceiling steps down halfway across stops being a rectangle from the
 * doorway, before you have walked into it.
 */
function buildSoffit(spec: LevelSpec): PropShape {
  const arch = acquireArch(spec);
  const pal = propPalette(spec);

  const bulkhead = (len: number, depth: number, drop: number, uo: number): WallParts => {
    const paper: THREE.BufferGeometry[] = [];
    const plain: THREE.BufferGeometry[] = [];
    const hx = len / 2;
    const hz = depth / 2;
    for (const side of [1, -1] as const) {
      // Metres, like every other UV handed to the building materials.
      const u = (a: number): number => uo + (side > 0 ? a : -a);
      paper.push(face(len, drop, 0, -drop / 2, side * hz, side > 0 ? 'pz' : 'nz', u(-hx), -drop, u(hx), 0));
    }
    plain.push(face(len, depth, 0, -drop, 0, 'ny', 0, 0, 1, 1));
    for (const end of [1, -1] as const) {
      plain.push(face(depth, drop, end * hx, -drop / 2, 0, end > 0 ? 'px' : 'nx', 0, 0, 1, 1));
    }
    return { paper, skirt: [], plain };
  };

  const plainRun = bulkhead(2.9, 0.5, 0.55, 0);
  const lit = bulkhead(2.9, 0.46, 0.62, 1.1);
  const wide = bulkhead(2.6, 0.68, 0.4, 2.2);
  // An access hatch, screwed shut, in a bulkhead with nothing behind it.
  wide.plain.push(box(0.46, 0.014, 0.4, 0.5, -0.407, 0));

  // Three downlights that have not been lit in a long time.
  const cans: THREE.BufferGeometry[] = [];
  for (const x of [-0.9, 0, 0.9]) cans.push(cyl(0.075, 0.075, 0.014, 8, x, -0.615, 0));

  return makeShape(
    spec,
    { kind: 'ceiling' },
    [...wallLayers(arch, [plainRun, lit, wide]), layer(matte(pal.dark, 0.01), [null, merge(cans), null])],
    [releaseArch(spec)]
  );
}

/**
 * Three columns set far too close together, holding up a ceiling that does not
 * need them. Papered and skirted like the level's own pillars, so the only thing
 * wrong is the spacing — and, in two of the three variants, one column.
 */
function buildColumnCluster(spec: LevelSpec): PropShape {
  const arch = acquireArch(spec);
  const H = spec.ceilingHeight;
  const W = 0.3;
  const at: ReadonlyArray<readonly [number, number]> = [
    [-0.4, -0.3],
    [0.05, 0.33],
    [0.41, -0.2],
  ];

  const upright: WallParts = { paper: [], skirt: [], plain: [] };
  for (const [x, z] of at) addParts(upright, buildingColumn(W, H, x, z));

  // One of them stops half a metre short of the ceiling and is capped off there.
  const short: WallParts = { paper: [], skirt: [], plain: [] };
  addParts(short, buildingColumn(W, H, at[0]![0], at[0]![1]));
  addParts(short, buildingColumn(W, H - 0.52, at[1]![0], at[1]![1], true));
  addParts(short, buildingColumn(W, H, at[2]![0], at[2]![1]));

  // One of them is out of plumb, which a column cannot be and still be doing
  // anything. Built at the origin so the lean pivots on its own foot.
  const leaning: WallParts = { paper: [], skirt: [], plain: [] };
  addParts(leaning, buildingColumn(W, H, at[0]![0], at[0]![1]));
  // Built a shade short: `anchorProp` scales the whole prop down to fit the
  // tallest thing in it, so a leaning column drawn over-height would take the
  // two upright ones with it and leave all three hanging.
  const tilted = buildingColumn(W, H - 0.03, 0, 0);
  tumble([...tilted.paper, ...tilted.skirt, ...tilted.plain], 0, 0.035);
  addParts(leaning, moveParts(tilted, at[1]![0], 0, at[1]![1]));
  addParts(leaning, buildingColumn(W, H, at[2]![0], at[2]![1]));

  return makeShape(spec, { kind: 'floor', yawJitter: 0.02 }, wallLayers(arch, [upright, short, leaning]), [
    releaseArch(spec),
  ]);
}

/** Four steps up to a blank wall. Solid all the way, so they cannot be climbed. */
function buildStairsNowhere(spec: LevelSpec): PropShape {
  const arch = acquireArch(spec);
  const pal = propPalette(spec);
  const W = 1.4;
  const RISE = 0.18;
  // Five risers of 0.18 make the 0.9 the footprint blocks; four goings of 0.24
  // plus a 0.14 landing at the top make its 1.1 of depth.
  const GOING = 0.24;
  const BACK = 0.55;

  /** Steps as a staircase of boxes, each sitting on the one below. `eat` takes the top tread's nosing off. */
  const flight = (steps: number, eat = 0): THREE.BufferGeometry[] => {
    const parts: THREE.BufferGeometry[] = [];
    for (let i = 0; i < steps; i++) {
      const front = -BACK + i * GOING + (i === steps - 1 ? eat : 0);
      parts.push(box(W, RISE, BACK - front, 0, i * RISE + RISE / 2, (front + BACK) / 2));
    }
    return parts;
  };
  const nosings = (steps: number): THREE.BufferGeometry[] => {
    const parts: THREE.BufferGeometry[] = [];
    for (let i = 0; i < steps; i++) {
      parts.push(box(W, 0.012, 0.05, 0, (i + 1) * RISE - 0.005, -BACK + i * GOING + 0.025));
    }
    return parts;
  };

  const five = merge(flight(5));
  const four = merge(flight(4));
  // The top step lost its front edge, and the piece of it is on the step below.
  const broken = merge([...flight(5, 0.07), ybox(0.34, 0.06, 0.18, -0.22, 4 * RISE + 0.03, 0.24, 0.42)]);

  // A handrail 0.85 above the pitch line, on the one side that is not a wall.
  const rail = merge([
    box(0.04, 0.85, 0.04, 0.64, 0.6, -BACK + 0.1),
    box(0.04, 0.85, 0.04, 0.64, 1.32, BACK - 0.1),
    strut(0.024, 0.64, 1.02, -BACK + 0.02, 0.64, 1.76, BACK - 0.02, 5),
  ]);

  return makeShape(
    spec,
    { kind: 'floor', yawJitter: 0.02 },
    [
      layer(arch.plain, [five, four, broken]),
      layer(arch.joineryDark, [merge(nosings(5)), merge(nosings(4)), merge(nosings(4))]),
      layer(matte(pal.metal), [null, rail, null]),
    ],
    [releaseArch(spec)]
  );
}

/**
 * A freestanding cubicle divider, standing in a room with no cubicles.
 *
 * This one is furniture rather than building, so it takes the prop palette: it
 * was carried in, and it belongs to whoever last moved the office round.
 */
function buildPartition(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const W = 1.66;
  const H = 1.55;

  const frame = (): THREE.BufferGeometry[] => [
    box(0.035, H, 0.05, -(W / 2 - 0.018), H / 2, 0),
    box(0.035, H, 0.05, W / 2 - 0.018, H / 2, 0),
    box(W, 0.04, 0.05, 0, H - 0.02, 0),
    box(W, 0.04, 0.05, 0, 0.09, 0),
    // T-feet, kept inside the footprint so nothing catches on a foot that is
    // drawn outside the box that blocks it.
    box(0.06, 0.09, 0.2, -(W / 2 - 0.1), 0.045, 0),
    box(0.06, 0.09, 0.2, W / 2 - 0.1, 0.045, 0),
  ];
  const panel = (): THREE.BufferGeometry => box(W - 0.09, H - 0.17, 0.038, 0, H / 2 + 0.02, 0);

  const plainF = merge(frame());
  const plainP = panel();

  const pinnedF = merge(frame());
  const pinnedP = panel();
  // A sheet of paper, pinned to a divider dividing nothing.
  const pinned = merge([box(0.21, 0.297, 0.004, -0.3, 1.06, -0.023)]);

  const tiltF = merge(frame());
  const tiltP = panel();
  // One foot has come off and it is resting on its corner.
  tumble([tiltF, tiltP], 0, 0.03);
  settle([tiltF, tiltP]);

  return makeShape(spec, { kind: 'floor', yawJitter: 0.02 }, [
    layer(matte(pal.metal), [plainF, pinnedF, tiltF]),
    layer(matte(pal.fabric), [plainP, pinnedP, tiltP]),
    layer(matte(pal.paper, 0.09), [null, pinned, null]),
  ]);
}

/**
 * An interior window, looking into a room that is dark.
 *
 * The pane is backed by an unlit black plane rather than by anything modelled.
 * Nothing is ever on the other side, and that is the point: an office borrowed
 * light window with no light behind it says there is a room there and refuses to
 * show it.
 */
function buildInnerWindow(spec: LevelSpec): PropShape {
  const arch = acquireArch(spec);
  const pal = propPalette(spec);
  const W = 0.95;
  const H = 0.8;
  const F = 0.06;
  // Everything lives in front of local z = 0. `Anchor.out` sets the wall face a
  // few millimetres behind that, and the wall is opaque and writes depth — a
  // reveal modelled *into* it would simply be depth-culled and the window would
  // show wallpaper.
  const D = 0.055;

  const surround = (mullion: boolean): THREE.BufferGeometry[] => {
    const parts = [
      box(F, H, D, -(W - F) / 2, 0, -D / 2),
      box(F, H, D, (W - F) / 2, 0, -D / 2),
      box(W, F, D, 0, (H - F) / 2, -D / 2),
      box(W, F, D, 0, -(H - F) / 2, -D / 2),
      // A sill, standing proud the way a cheap one always does.
      box(W + 0.08, 0.045, 0.1, 0, -H / 2 - 0.02, -0.05),
    ];
    if (mullion) parts.push(box(0.045, H - F * 2, D - 0.02, 0, 0, -(D - 0.02) / 2));
    return parts;
  };

  const opening = W - F * 2;
  const glass = box(opening, H - F * 2, 0.006, 0, 0, -0.028);
  const glassSplit = merge([
    box(opening / 2 - 0.03, H - F * 2, 0.006, -(opening / 4 + 0.015), 0, -0.028),
    box(opening / 2 - 0.03, H - F * 2, 0.006, opening / 4 + 0.015, 0, -0.028),
  ]);
  // Where the pane went, and what is left round the edge of the frame.
  const shards = merge([
    box(opening, 0.05, 0.006, 0, (H - F) / 2 - 0.06, -0.028),
    box(0.05, H - F * 2, 0.006, -(opening / 2 - 0.03), 0, -0.028),
  ]);

  // The room on the other side. It is never lit and never modelled.
  const dark = box(opening, H - F * 2, 0.004, 0, 0, -0.004);

  return makeShape(
    spec,
    { kind: 'wall', height: 1.3, out: WALL_INSET },
    [
      layer(arch.joinery, [merge(surround(false)), merge(surround(true)), merge(surround(false))]),
      layer(arch.dark, [dark, dark.clone(), dark.clone()]),
      layer(matte(pal.glass, 0.03), [glass, glassSplit, shards]),
    ],
    [releaseArch(spec)]
  );
}

/* ================================================================== *
 * Objects that should not be here
 *
 * Presented without comment, at the finish of cheap commercial fit-out:
 * stained, dusty, a bit yellowed by the light, nothing new and nothing glossy.
 * A bathtub plumbed into an office floor is unsettling because it is ordinary
 * and in the wrong room, so none of these is stylised and none of them is a
 * joke. Silhouette is the whole budget — most of the game is fogged, and at
 * eight metres a bathtub is a rounded trough on four feet and nothing else.
 * ================================================================== */

/** A cast iron bath, plumbed into a floor with nothing under it. */
function buildBathtub(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const RIM = 0.56;

  const shell = (): THREE.BufferGeometry[] => [
    box(0.9, 0.42, 0.8, 0, 0.35, 0),
    cyl(0.4, 0.38, 0.42, 10, -0.45, 0.35, 0),
    cyl(0.4, 0.38, 0.42, 10, 0.45, 0.35, 0),
    // Feet. Short, and only just clear of the floor.
    box(0.09, 0.16, 0.09, -0.6, 0.08, -0.28),
    box(0.09, 0.16, 0.09, 0.6, 0.08, -0.28),
    box(0.09, 0.16, 0.09, -0.6, 0.08, 0.28),
    box(0.09, 0.16, 0.09, 0.6, 0.08, 0.28),
  ];
  // The interior is a dark slab standing a few millimetres proud of the rim
  // rather than a modelled cavity: a bath is only ever seen from above and the
  // dark rectangle inside a pale surround is the entire read at this range. The
  // scum ring sits between the two, so what shows of it is the band of dried
  // residue around the top of the basin.
  const inside = (): THREE.BufferGeometry => box(1.36, 0.24, 0.52, 0, RIM - 0.113, 0);
  const scum = (): THREE.BufferGeometry => box(1.42, 0.02, 0.58, 0, RIM - 0.0075, 0);

  const plainTub = merge(shell());
  const chromed = merge([
    // Spout and two taps, over the end.
    strut(0.022, -0.66, 0.72, 0, -0.5, 0.72, 0, 6),
    cyl(0.03, 0.03, 0.1, 6, -0.66, 0.66, 0),
    cyl(0.035, 0.035, 0.035, 6, -0.62, 0.62, -0.14),
    cyl(0.035, 0.035, 0.035, 6, -0.62, 0.62, 0.14),
  ]);

  const strippedTub = merge(shell());
  // The waste, run down through a floor that has nothing under it.
  const standpipe = merge([
    strut(0.028, 0.72, 0.34, 0, 0.72, 0, 0, 6),
    cyl(0.05, 0.05, 0.03, 8, 0.72, 0.02, 0),
    box(0.05, 0.05, 0.16, 0.72, 0.3, 0),
  ]);

  const showerTub = merge(shell());
  const riser = merge([
    strut(0.02, -0.7, 0.55, 0.24, -0.7, 1.78, 0.24, 6),
    strut(0.02, -0.7, 1.78, 0.24, -0.7, 1.78, 0.04, 6),
    cyl(0.075, 0.055, 0.05, 8, -0.7, 1.74, 0.04),
  ]);

  return makeShape(spec, { kind: 'floor', yawJitter: 0.05 }, [
    layer(matte(pal.pale), [plainTub, strippedTub, showerTub]),
    layer(matte(pal.dark, 0.02), [inside(), inside(), inside()]),
    layer(matte(pal.grime, 0.03), [scum(), scum(), scum()]),
    layer(matte(pal.chrome, 0.09), [chromed, standpipe, riser]),
  ]);
}

/** Three televisions, stacked by somebody who had three televisions. */
function buildTvStack(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const W = 0.6;
  const HGT = 0.42;
  const D = 0.52;

  const set = (x: number, y: number, z: number, yaw: number): THREE.BufferGeometry[] => {
    const f = faceOffset(yaw, D / 2);
    return [ybox(W, HGT, D, x, y + HGT / 2, z, yaw), ybox(W - 0.1, HGT - 0.08, 0.04, x + f.dx, y + HGT / 2 + 0.01, z + f.dz, yaw)];
  };
  const screen = (x: number, y: number, z: number, yaw: number): THREE.BufferGeometry => {
    const f = faceOffset(yaw, D / 2 + 0.02);
    return ybox(W - 0.14, HGT - 0.12, 0.02, x + f.dx, y + HGT / 2 + 0.01, z + f.dz, yaw);
  };

  const squareBody = merge([...set(0, 0, 0, 0), ...set(0, HGT, 0, 0), ...set(0, HGT * 2, 0, 0)]);
  const squareGlass = merge([screen(0, 0, 0, 0), screen(0, HGT, 0, 0), screen(0, HGT * 2, 0, 0)]);

  const askewBody = merge([...set(0, 0, 0, 0.06), ...set(-0.02, HGT, 0.01, -0.14), ...set(0.03, HGT * 2, -0.01, Math.PI + 0.1)]);
  const askewGlass = merge([screen(0, 0, 0, 0.06), screen(-0.02, HGT, 0.01, -0.14), screen(0.03, HGT * 2, -0.01, Math.PI + 0.1)]);

  const pairBody = merge([...set(-0.02, 0, 0, 0.04), ...set(0, HGT, -0.01, -0.08)]);
  const pairGlass = merge([screen(-0.02, 0, 0, 0.04), screen(0, HGT, -0.01, -0.08)]);
  // The third one is on its back on top of the other two, screen up.
  const flatBody = merge(set(0, 0, 0, 0.12));
  const flatGlass = merge([screen(0, 0, 0, 0.12)]);
  tumble([flatBody, flatGlass], -Math.PI / 2, 0.04);
  settle([flatBody, flatGlass]);
  centreZ([flatBody, flatGlass]);
  for (const g of [flatBody, flatGlass]) g.translate(0.01, HGT * 2 - 0.01, 0);

  return makeShape(spec, { kind: 'floor', yawJitter: 0.06 }, [
    layer(matte(pal.plasticDark), [squareBody, askewBody, merge([pairBody, flatBody])]),
    layer(matte(pal.glass, 0.02), [squareGlass, askewGlass, merge([pairGlass, flatGlass])]),
  ]);
}

/** A telephone box, standing in the middle of a carpeted floor. */
function buildPhoneBooth(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const HALF = 0.45;
  const H = 2.3;
  const POST = 0.07;

  const frame = (): THREE.BufferGeometry[] => {
    const parts = [
      box(HALF * 2, 0.12, HALF * 2, 0, 0.06, 0),
      box(HALF * 2 + 0.04, 0.1, HALF * 2 + 0.04, 0, H - 0.05, 0),
    ];
    for (const sx of [-1, 1] as const) {
      for (const sz of [-1, 1] as const) {
        parts.push(box(POST, H - 0.22, POST, sx * (HALF - POST / 2), 0.12 + (H - 0.22) / 2, sz * (HALF - POST / 2)));
      }
    }
    // Head and foot rails on the three glazed sides.
    for (const [x, z, w, d] of [
      [0, HALF - 0.02, HALF * 2 - POST, 0.03],
      [-(HALF - 0.02), 0, 0.03, HALF * 2 - POST],
      [HALF - 0.02, 0, 0.03, HALF * 2 - POST],
    ] as const) {
      parts.push(box(w, 0.05, d, x, 2.14, z), box(w, 0.05, d, x, 0.36, z));
    }
    return parts;
  };

  /** `sides` picks which of back, left, right and front still have glass in them. */
  const glaze = (sides: readonly number[]): THREE.BufferGeometry[] => {
    const all = [
      (): THREE.BufferGeometry => box(HALF * 2 - POST * 2, 1.74, 0.012, 0, 1.26, HALF - 0.03),
      (): THREE.BufferGeometry => box(0.012, 1.74, HALF * 2 - POST * 2, -(HALF - 0.03), 1.26, 0),
      (): THREE.BufferGeometry => box(0.012, 1.74, HALF * 2 - POST * 2, HALF - 0.03, 1.26, 0),
      (): THREE.BufferGeometry => box(HALF * 2 - POST * 2 - 0.04, 1.7, 0.012, 0, 1.26, -(HALF - 0.03)),
    ];
    return sides.map((i) => all[i]!());
  };

  // The phone itself, on the back panel. Handset off the hook in all of them.
  const phone = merge([
    box(0.24, 0.42, 0.1, 0, 1.34, HALF - 0.12),
    box(0.3, 0.03, 0.2, 0, 1.06, HALF - 0.16),
    box(0.06, 0.2, 0.05, -0.16, 0.78, HALF - 0.16),
    strut(0.008, -0.11, 1.18, HALF - 0.14, -0.16, 0.9, HALF - 0.16, 4),
  ]);

  const shut = merge(frame());
  const open = merge([...frame(), box(0.05, 1.9, 0.05, -(HALF - 0.12), 1.13, -(HALF - 0.1))]);
  const wrecked = merge(frame());

  return makeShape(spec, { kind: 'floor', yawJitter: 0.06 }, [
    layer(matte(pal.metalDark), [shut, open, wrecked]),
    layer(pane(pal.glass, 0.42), [merge(glaze([0, 1, 2, 3])), merge(glaze([0, 1, 2])), merge(glaze([0, 2]))]),
    layer(matte(pal.dark), [phone, phone.clone(), phone.clone()]),
  ]);
}

/** A shopping trolley, four hundred metres from anywhere that sells anything. */
function buildShoppingCart(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const BW = 0.55;
  const BL = 0.85;
  const BASE = 0.52;

  const basket = (): THREE.BufferGeometry[] => [
    box(BW, 0.02, BL, 0, BASE, 0),
    box(0.02, 0.32, BL, -BW / 2, BASE + 0.16, 0),
    box(0.02, 0.32, BL, BW / 2, BASE + 0.16, 0),
    box(BW, 0.3, 0.02, 0, BASE + 0.15, -BL / 2),
    box(BW, 0.34, 0.02, 0, BASE + 0.17, BL / 2),
    // The rim, which is the only part of a trolley that ever reads at distance.
    rodX(0.014, BW, 6, 0, BASE + 0.33, -BL / 2),
    rodX(0.014, BW, 6, 0, BASE + 0.35, BL / 2),
    box(0.016, 0.016, BL, -BW / 2, BASE + 0.34, 0),
    box(0.016, 0.016, BL, BW / 2, BASE + 0.34, 0),
    rodX(0.018, BW + 0.04, 6, 0, BASE + 0.44, BL / 2 + 0.06),
    // The tray underneath, and the legs down to it.
    box(BW - 0.08, 0.018, BL - 0.2, 0, 0.16, 0),
  ];
  const legs = (): THREE.BufferGeometry[] => {
    const parts: THREE.BufferGeometry[] = [];
    for (const sx of [-1, 1] as const) {
      for (const sz of [-1, 1] as const) {
        const x = sx * (BW / 2 - 0.03);
        const z = sz * (BL / 2 - 0.06);
        parts.push(strut(0.014, x, BASE, z, x * 0.86, 0.08, z * 0.94, 5));
      }
    }
    return parts;
  };
  const castors = (all: boolean): THREE.BufferGeometry[] => {
    const parts: THREE.BufferGeometry[] = [];
    for (const sx of [-1, 1] as const) {
      for (const sz of [-1, 1] as const) {
        // One castor is missing on the tired one, which is why it sits crooked.
        if (!all && sx < 0 && sz < 0) continue;
        parts.push(rodX(0.05, 0.03, 8, sx * (BW / 2 - 0.06), 0.05, sz * (BL / 2 - 0.08)));
      }
    }
    return parts;
  };

  const upB = merge([...basket(), ...legs()]);
  const upW = merge(castors(true));

  // Tipped forward onto its nose rather than rolled onto its side. A trolley on
  // its side is a metre across and the box in `PROP_DEFS` is 0.64; going over
  // the front puts its height into the depth it does have.
  const sideB = merge([...basket(), ...legs()]);
  const sideW = merge(castors(true));
  tumble([sideB, sideW], 1.52, 0.09, 0.15);
  settle([sideB, sideW]);
  centreZ([sideB, sideW]);

  const tiredB = merge([
    ...basket(),
    ...legs(),
    // The child seat, folded down and never folded back.
    box(BW - 0.06, 0.02, 0.26, 0, BASE + 0.04, BL / 2 - 0.16),
  ]);
  const tiredW = merge(castors(false));
  tumble([tiredB, tiredW], 0.05, -0.07);
  settle([tiredB, tiredW]);

  return makeShape(spec, { kind: 'floor', yawJitter: 0.15 }, [
    layer(matte(pal.metal), [upB, sideB, tiredB]),
    layer(matte(pal.plasticDark), [upW, sideW, tiredW]),
  ]);
}

/** A mattress, off a bed that is not here, leaned where it would be in the way. */
function buildMattress(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);

  const slabOf = (w: number, h: number, t: number): THREE.BufferGeometry[] => [
    box(w, h, t, 0, h / 2, 0),
    // Piped edges. Two thin rails are the difference between a mattress and a
    // sheet of plasterboard at this distance.
    box(w, 0.035, t + 0.02, 0, 0.03, 0),
    box(w, 0.035, t + 0.02, 0, h - 0.03, 0),
  ];

  /** A stain, on the face that is turned to the room. Leaned with the rest. */
  const mark = (w: number, h: number, x: number, y: number, t: number): THREE.BufferGeometry =>
    box(w, h, 0.008, x, y, -(t / 2 + 0.006));

  // The lean angles are held down by the footprint: 0.56 m of depth minus the
  // mattress's own thickness is all the room a two-metre slab has to lie back
  // into, which works out at about ten degrees.
  const single = slabOf(1.32, 1.86, 0.2);
  const singleStain = [mark(0.6, 0.52, 0.2, 1.1, 0.2)];
  lean([...single, ...singleStain], 0.17, -0.16);

  const thick = slabOf(1.36, 1.9, 0.26);
  const thickStain = [mark(0.5, 0.7, -0.28, 0.9, 0.26)];
  lean([...thick, ...thickStain], 0.15, -0.15);

  // This one slid down the wall and buckled where it met the floor.
  const slumped = slabOf(1.34, 1.5, 0.22);
  const slumpedStain = [mark(0.7, 0.4, 0.1, 0.8, 0.22)];
  lean([...slumped, ...slumpedStain], 0.22, -0.17);
  slumped.push(box(1.34, 0.24, 0.3, 0, 0.12, -0.12));

  return makeShape(spec, { kind: 'floor', yawJitter: 0.08 }, [
    layer(matte(pal.pale), [merge(single), merge(thick), merge(slumped)]),
    layer(matte(pal.grime, 0.02), [merge(singleStain), merge(thickStain), merge(slumpedStain)]),
  ]);
}

/** An upright piano. Nobody has tuned it and nobody is going to. */
function buildPiano(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const W = 1.5;
  const H = 1.25;

  // The whole thing has to live inside 0.7 m of depth, so the case is set back
  // and the keybed uses the rest: an upright really is about this deep.
  const BACK = 0.12;

  const carcase = (lidOpen: boolean): THREE.BufferGeometry[] => {
    const parts = [
      box(W, 1.13, 0.44, 0, 0.68, BACK),
      box(W + 0.06, 0.08, 0.5, 0, 0.04, BACK - 0.02),
      // The keybed, standing out in front of the case.
      box(W - 0.1, 0.06, 0.26, 0, 0.72, -0.21),
      box(W - 0.1, 0.16, 0.03, 0, 0.63, -0.325),
      box(0.1, 0.62, 0.12, -(W / 2 - 0.12), 0.4, -0.18),
      box(0.1, 0.62, 0.12, W / 2 - 0.12, 0.4, -0.18),
    ];
    if (lidOpen) {
      const lid = box(W + 0.04, 0.045, 0.46);
      lid.rotateX(-0.42);
      lid.translate(0, H - 0.09, BACK + 0.06);
      parts.push(lid);
    } else {
      parts.push(box(W + 0.04, 0.045, 0.5, 0, H - 0.02, BACK));
    }
    return parts;
  };

  const fallboardShut = box(W - 0.1, 0.2, 0.05, 0, 0.85, -0.25);
  const fallboardOpen = (): THREE.BufferGeometry => box(W - 0.1, 0.05, 0.2, 0, 0.94, -0.13);

  const keys = (missing: boolean): THREE.BufferGeometry[] => {
    const parts = [box(W - 0.16, 0.028, 0.22, 0, 0.766, -0.21)];
    // Four keys gone. It is the gap that reads at this distance, not the keys.
    if (missing) parts.push(box(0.16, 0.03, 0.23, 0.24, 0.76, -0.21));
    return parts;
  };
  const blackKeys = (): THREE.BufferGeometry[] => {
    const parts: THREE.BufferGeometry[] = [];
    for (let i = 0; i < 9; i++) parts.push(box(0.022, 0.022, 0.14, -0.6 + i * 0.15, 0.79, -0.25));
    return parts;
  };

  const pedals = (): THREE.BufferGeometry[] => [
    box(0.2, 0.26, 0.04, 0, 0.17, -0.05),
    box(0.03, 0.012, 0.12, -0.05, 0.09, -0.09),
    box(0.03, 0.012, 0.12, 0.05, 0.09, -0.09),
  ];

  return makeShape(spec, { kind: 'floor', yawJitter: 0.03 }, [
    layer(matte(pal.woodDark), [merge([...carcase(false), fallboardShut]), merge(carcase(false)), merge(carcase(true))]),
    layer(matte(pal.pale), [null, merge(keys(false)), merge(keys(true))]),
    layer(matte(pal.dark), [
      merge(pedals()),
      merge([...pedals(), ...blackKeys(), fallboardOpen()]),
      // The music desk, propped up over an open lid with nothing on it.
      merge([...pedals(), ...blackKeys(), fallboardOpen(), box(W - 0.14, 0.5, 0.02, 0, 0.98, -0.09)]),
    ]),
  ]);
}

/** A dry fish tank with a residue line where the water gave up. */
function buildFishTank(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const W = 0.96;
  const D = 0.46;
  const STAND = 0.62;
  const TANK = 0.5;

  const stand = (): THREE.BufferGeometry[] => [
    box(W, STAND - 0.06, D, 0, (STAND - 0.06) / 2 + 0.06, 0),
    box(W + 0.04, 0.06, D + 0.04, 0, STAND - 0.03, 0),
    box(W - 0.16, 0.03, 0.02, 0, 0.03, -D / 2),
  ];
  const frame = (posts: boolean): THREE.BufferGeometry[] => {
    const parts = [
      box(W - 0.02, 0.05, D - 0.02, 0, STAND + 0.025, 0),
      box(W - 0.02, 0.05, D - 0.02, 0, STAND + TANK - 0.025, 0),
    ];
    // The corner posts on the open face went with the glass that was in it.
    for (const sz of posts ? ([-1, 1] as const) : ([1] as const)) {
      parts.push(
        box(0.04, TANK, 0.04, -(W / 2 - 0.04), STAND + TANK / 2, sz * (D / 2 - 0.04)),
        box(0.04, TANK, 0.04, W / 2 - 0.04, STAND + TANK / 2, sz * (D / 2 - 0.04))
      );
    }
    return parts;
  };
  const glassPanes = (front: boolean): THREE.BufferGeometry[] => {
    const parts = [
      box(W - 0.1, TANK - 0.08, 0.008, 0, STAND + TANK / 2, D / 2 - 0.03),
      box(0.008, TANK - 0.08, D - 0.1, -(W / 2 - 0.03), STAND + TANK / 2, 0),
      box(0.008, TANK - 0.08, D - 0.1, W / 2 - 0.03, STAND + TANK / 2, 0),
    ];
    if (front) parts.push(box(W - 0.1, TANK - 0.08, 0.008, 0, STAND + TANK / 2, -(D / 2 - 0.03)));
    return parts;
  };

  // Gravel, an ornament, and the tide mark two thirds up where it last stood.
  const contents = (): THREE.BufferGeometry[] => [
    box(W - 0.12, 0.05, D - 0.12, 0, STAND + 0.075, 0),
    box(0.12, 0.14, 0.1, -0.24, STAND + 0.12, 0.04),
  ];
  const residue = (): THREE.BufferGeometry[] => [
    box(W - 0.11, 0.018, D - 0.11, 0, STAND + TANK * 0.66, 0),
    box(W - 0.13, 0.01, D - 0.13, 0, STAND + TANK * 0.44, 0),
  ];

  const hood = box(W + 0.02, 0.07, D + 0.02, 0, STAND + TANK + 0.035, 0);

  return makeShape(spec, { kind: 'floor', yawJitter: 0.06 }, [
    layer(matte(pal.woodDark), [merge([...stand(), hood]), merge(stand()), merge(stand())]),
    layer(matte(pal.plasticDark), [merge(frame(true)), merge(frame(true)), merge(frame(false))]),
    layer(pane(pal.glass, 0.3), [merge(glassPanes(true)), merge(glassPanes(true)), merge(glassPanes(false))]),
    layer(matte(pal.grime, 0.03), [
      merge([...contents(), ...residue()]),
      merge([...contents(), ...residue()]),
      merge(contents()),
    ]),
  ]);
}

/** A chandelier, in a suspended ceiling. None of the lamps work. */
function buildChandelier(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const DROP = 0.48;
  const R = 0.24;

  const stem = (): THREE.BufferGeometry[] => [
    cyl(0.075, 0.085, 0.03, 8, 0, -0.015, 0),
    strut(0.008, 0, -0.03, 0, 0, -DROP + 0.06, 0, 4),
    cyl(0.05, 0.038, 0.14, 8, 0, -DROP + 0.01, 0),
    ring(R, 0.014, 10, 0, -DROP - 0.06, 0, true),
  ];
  const arm = (i: number, n: number, out: number): THREE.BufferGeometry[] => {
    const a = (i / n) * Math.PI * 2;
    const x = Math.cos(a) * out;
    const z = Math.sin(a) * out;
    return [
      strut(0.009, 0, -DROP - 0.02, 0, x, -DROP - 0.06, z, 4),
      cyl(0.038, 0.028, 0.045, 6, x, -DROP - 0.03, z),
    ];
  };
  const candle = (i: number, n: number, out: number): THREE.BufferGeometry[] => {
    const a = (i / n) * Math.PI * 2;
    const x = Math.cos(a) * out;
    const z = Math.sin(a) * out;
    return [cyl(0.019, 0.021, 0.1, 6, x, -DROP + 0.04, z), blob(0.028, 0.036, 0.028, x, -DROP + 0.11, z)];
  };
  const drop = (i: number, n: number, out: number, len: number): THREE.BufferGeometry => {
    const a = (i / n) * Math.PI * 2 + 0.3;
    return blob(0.018, len, 0.018, Math.cos(a) * out, -DROP - 0.06 - len, Math.sin(a) * out);
  };

  const full: THREE.BufferGeometry[] = [...stem()];
  const fullGlass: THREE.BufferGeometry[] = [];
  for (let i = 0; i < 6; i++) full.push(...arm(i, 6, R));
  const fullCandles: THREE.BufferGeometry[] = [];
  for (let i = 0; i < 6; i++) fullCandles.push(...candle(i, 6, R));
  for (let i = 0; i < 8; i++) fullGlass.push(drop(i, 8, R * 0.92, 0.05 + (i % 3) * 0.015));

  const brokenFrame: THREE.BufferGeometry[] = [...stem()];
  const brokenCandles: THREE.BufferGeometry[] = [];
  for (const i of [0, 1, 3, 5]) brokenFrame.push(...arm(i, 6, R));
  for (const i of [0, 3, 5]) brokenCandles.push(...candle(i, 6, R));
  const brokenGlass: THREE.BufferGeometry[] = [];
  for (const i of [1, 4, 6]) brokenGlass.push(drop(i, 8, R * 0.92, 0.05));
  // It has been knocked and hangs off true. Pivoting on the rose, which is the
  // origin, is what keeps it attached to the ceiling while it does.
  tumble([...brokenFrame, ...brokenCandles, ...brokenGlass], 0.06, 0.15);

  const strippedFrame: THREE.BufferGeometry[] = [...stem()];
  for (let i = 0; i < 6; i++) strippedFrame.push(...arm(i, 6, R));

  return makeShape(spec, { kind: 'ceiling', yawJitter: Math.PI }, [
    layer(matte(pal.metal, 0.07), [merge(full), merge(brokenFrame), merge(strippedFrame)]),
    layer(matte(pal.pale), [merge(fullCandles), merge(brokenCandles), null]),
    layer(matte(pal.glass, 0.06), [merge(fullGlass), merge(brokenGlass), null]),
  ]);
}

/**
 * A shop mannequin, faceless, turned a little away from whoever is looking.
 *
 * The stance is the whole prop. Square-on it is a dummy; with the hips one way,
 * the shoulders another and the head further round again, it reads for a moment
 * as somebody standing very still with their back half turned — which is
 * exactly long enough.
 */
function buildMannequin(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);

  const stand = (): THREE.BufferGeometry[] => [
    cyl(0.17, 0.18, 0.025, 12, 0, 0.012, 0),
    cyl(0.016, 0.016, 0.16, 6, 0, 0.1, 0),
  ];

  /** Hips, waist, chest and shoulders each turned further than the last. */
  const body = (twist: number): THREE.BufferGeometry[] => [
    cyl(0.062, 0.048, 0.76, 7, -0.075, 0.4, 0),
    cyl(0.062, 0.048, 0.76, 7, 0.075, 0.4, 0),
    ybox(0.26, 0.22, 0.19, 0, 0.86, 0, 0),
    ybox(0.22, 0.18, 0.16, 0, 1.04, 0, twist * 0.5),
    ybox(0.3, 0.36, 0.2, 0, 1.3, 0, twist),
    ybox(0.36, 0.09, 0.17, 0, 1.5, 0, twist),
    cyl(0.038, 0.042, 0.09, 6, 0, 1.56, 0),
  ];

  const head = (twist: number, bow: number): THREE.BufferGeometry => {
    const g = blob(0.085, 0.115, 0.098, 0, 0, 0);
    g.rotateX(bow);
    g.rotateY(twist);
    g.translate(0, 1.69, 0);
    return g;
  };

  const armPair = (twist: number, sides: readonly number[]): THREE.BufferGeometry[] => {
    const parts: THREE.BufferGeometry[] = [];
    for (const s of sides) {
      const sx = s * 0.19;
      parts.push(
        strut(0.042, sx, 1.48, 0, sx * 1.06, 1.16, 0.02, 6),
        strut(0.034, sx * 1.06, 1.16, 0.02, sx * 1.02, 0.88, -0.03, 6),
        blob(0.045, 0.05, 0.035, sx * 1.02, 0.86, -0.04)
      );
    }
    // Turned with the shoulders, so the arms hang where the chest points.
    tumble(parts, 0, 0, twist);
    return parts;
  };

  const turned = merge([...stand(), ...body(0.5), ...armPair(0.5, [-1, 1]), head(0.95, 0.05)]);

  // One arm only. The other socket is open and nobody put it back.
  const bowed = merge([...stand(), ...body(-0.34), ...armPair(-0.34, [1]), head(-0.7, 0.22)]);

  const headless = merge([...stand(), ...body(0.2), ...armPair(0.2, [-1, 1])]);

    // Small wobble on purpose. The turn is baked into the stance, and the yaw
  // jitter is render-only — `PropIndex` rotates the collision box by the spot's
  // own yaw — so a big one walks the shoulders out of the box that blocks them.
  return makeShape(spec, { kind: 'floor', yawJitter: 0.12 }, solo(matte(pal.pale), [turned, bowed, headless]));
}

/** Stacking chairs, stacked. The stack is always higher than it needs to be. */
function buildChairPile(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);

  const chair = (y: number, dx: number, dz: number, yaw: number): { shell: THREE.BufferGeometry[]; frame: THREE.BufferGeometry[] } => ({
    shell: [ybox(0.44, 0.04, 0.42, dx, y + 0.44, dz, yaw), ybox(0.42, 0.4, 0.03, dx + Math.sin(yaw) * 0.19, y + 0.66, dz + Math.cos(yaw) * 0.19, yaw)],
    frame: [
      ybox(0.03, 0.44, 0.03, dx - 0.18, y + 0.22, dz - 0.17, yaw),
      ybox(0.03, 0.44, 0.03, dx + 0.18, y + 0.22, dz - 0.17, yaw),
      ybox(0.03, 0.44, 0.03, dx - 0.18, y + 0.22, dz + 0.17, yaw),
      ybox(0.03, 0.44, 0.03, dx + 0.18, y + 0.22, dz + 0.17, yaw),
    ],
  });

  const stack = (n: number, x0: number, z0: number, seed: number): { shell: THREE.BufferGeometry[]; frame: THREE.BufferGeometry[] } => {
    const shell: THREE.BufferGeometry[] = [];
    const frame: THREE.BufferGeometry[] = [];
    const rng = makeRNG(seed);
    for (let i = 0; i < n; i++) {
      const c = chair(i * 0.09, x0 + rng.range(-0.035, 0.035), z0 + rng.range(-0.03, 0.03) - i * 0.012, rng.range(-0.1, 0.1));
      shell.push(...c.shell);
      frame.push(...c.frame);
    }
    return { shell, frame };
  };

  const low = stack(4, 0, 0, 0x4a17);
  const mid = stack(7, 0, 0, 0x91c3);
  const tall = stack(10, -0.16, 0.02, 0x2f80);
  const beside = stack(3, 0.3, -0.12, 0x77b1);

  return makeShape(spec, { kind: 'floor', yawJitter: 0.12 }, [
    layer(matte(pal.plastic), [merge(low.shell), merge(mid.shell), merge([...tall.shell, ...beside.shell])]),
    layer(matte(pal.metalDark), [merge(low.frame), merge(mid.frame), merge([...tall.frame, ...beside.frame])]),
  ]);
}

/** Doors, off their frames, stacked against a wall. */
function buildDoorPile(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);

  const leaf = (angle: number, z: number, dx: number, w: number, h: number): THREE.BufferGeometry => {
    const g = box(w, h, 0.042, dx, h / 2, 0);
    g.rotateX(angle);
    g.translate(0, 0, z);
    return g;
  };

  // A two-metre leaf leaning with its foot on one edge of the footprint and its
  // head on the other is at 0.43 rad, so nothing here goes past that: any more
  // and the pile is standing outside the box that blocks it.
  const three = [leaf(0.4, -0.4, 0, 0.78, 1.98), leaf(0.37, -0.34, 0.03, 0.76, 1.94), leaf(0.34, -0.28, -0.02, 0.8, 1.99)];
  const threeEdge = [leaf(0.43, -0.42, -0.01, 0.79, 1.97)];

  const five = [
    leaf(0.41, -0.4, 0, 0.78, 1.98),
    leaf(0.39, -0.35, 0.02, 0.76, 1.9),
    leaf(0.36, -0.3, -0.03, 0.8, 1.96),
    leaf(0.33, -0.25, 0.01, 0.74, 1.88),
  ];
  // The front one has slipped and is on its way to the floor.
  const fiveEdge = [leaf(0.44, -0.42, 0.04, 0.78, 1.94)];

  const pair = [leaf(0.36, -0.36, -0.02, 0.78, 1.98), leaf(0.33, -0.3, 0.02, 0.76, 1.95)];
  // Two frames came off with them, still screwed together at the head.
  const pairEdge = [
    box(0.05, 1.98, 0.05, -0.42, 0.99, -0.2),
    box(0.05, 1.98, 0.05, 0.42, 0.99, -0.18),
    box(0.9, 0.05, 0.05, 0, 1.96, -0.19),
  ];

  settle([...three, ...threeEdge]);
  settle([...five, ...fiveEdge]);
  settle([...pair, ...pairEdge]);

  return makeShape(spec, { kind: 'floor', yawJitter: 0.09 }, [
    layer(matte(pal.wood), [merge(three), merge(five), merge(pair)]),
    layer(matte(pal.woodDark), [merge(threeEdge), merge(fiveEdge), merge(pairEdge)]),
  ]);
}

/** An office desk, out in the open, at no angle to anything. */
function buildDesk(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const W = 1.44;
  const D = 0.72;
  const TOP = 0.72;

  const top = (): THREE.BufferGeometry[] => [
    box(W, 0.04, D, 0, TOP + 0.02, 0),
    box(W, 0.02, D + 0.01, 0, TOP - 0.01, 0),
  ];
  const legs = (): THREE.BufferGeometry[] => [
    box(0.05, TOP, 0.05, W / 2 - 0.06, TOP / 2, -(D / 2 - 0.07)),
    box(0.05, TOP, 0.05, W / 2 - 0.06, TOP / 2, D / 2 - 0.07),
    box(0.04, 0.04, D - 0.16, W / 2 - 0.06, 0.09, 0),
    box(0.9, 0.36, 0.02, -0.2, 0.46, D / 2 - 0.04),
  ];
  const pedestal = (out: number): THREE.BufferGeometry[] => [
    box(0.42, TOP - 0.08, 0.62, -(W / 2 - 0.25), (TOP - 0.08) / 2 + 0.06, 0),
    box(0.4, 0.19, 0.02, -(W / 2 - 0.25), 0.6, -(0.31 + out)),
    box(0.4, 0.19, 0.02, -(W / 2 - 0.25), 0.4, -0.31),
    box(0.4, 0.19, 0.02, -(W / 2 - 0.25), 0.2, -0.31),
  ];
  const handles = (out: number): THREE.BufferGeometry[] => [
    box(0.16, 0.02, 0.024, -(W / 2 - 0.25), 0.66, -(0.325 + out)),
    box(0.16, 0.02, 0.024, -(W / 2 - 0.25), 0.46, -0.325),
    box(0.16, 0.02, 0.024, -(W / 2 - 0.25), 0.26, -0.325),
  ];

  const plainTop = merge(top());
  const plainBody = merge([...legs(), ...pedestal(0)]);

  const openTop = merge(top());
  const openBody = merge([
    ...legs(),
    ...pedestal(0.06),
    // The drawer itself, pulled out behind its front.
    box(0.38, 0.17, 0.24, -(W / 2 - 0.25), 0.6, -0.28),
  ]);
  const openPaper = merge([
    slab(0.21, 0.297, 0.24, TOP + 0.041, -0.06, 0.4),
    slab(0.21, 0.297, 0.3, TOP + 0.043, 0.02, 0.9),
    slab(0.21, 0.297, -0.42, TOP + 0.041, 0.18, -0.3),
  ]);

  const sagTop = merge(top());
  const sagBody = merge([...legs(), ...pedestal(0)]);
  // One leg has splayed and the top is riding a couple of degrees off level.
  tumble([sagTop], 0, 0.028);
  sagTop.translate(0, 0.01, 0);

  return makeShape(spec, { kind: 'floor', yawJitter: 0.03 }, [
    layer(matte(pal.wood), [plainTop, openTop, sagTop]),
    layer(matte(pal.metal), [plainBody, openBody, sagBody]),
    layer(matte(pal.dark), [merge(handles(0)), merge(handles(0.06)), merge(handles(0))]),
    layer(matte(pal.paper, 0.09), [null, openPaper, null]),
  ]);
}

/** A bookshelf. What is on it is not worth reading and never was. */
function buildBookshelf(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const W = 0.9;
  const H = 1.9;
  const D = 0.3;
  const SHELVES = [0.42, 0.76, 1.1, 1.44, 1.76];

  const carcase = (bent: number): THREE.BufferGeometry[] => {
    const parts = [
      box(0.03, H, D, -(W / 2 - 0.015), H / 2, 0),
      box(0.03, H, D, W / 2 - 0.015, H / 2, 0),
      box(W, 0.03, D, 0, H - 0.015, 0),
      box(W, 0.1, D, 0, 0.05, 0),
      box(W - 0.06, H - 0.16, 0.008, 0, H / 2 + 0.04, D / 2 - 0.006),
    ];
    for (let i = 0; i < SHELVES.length; i++) {
      const g = box(W - 0.06, 0.022, D - 0.03);
      // The one that gave way is still up there, at an angle it cannot hold.
      if (i === bent) g.rotateZ(0.055);
      g.translate(0, SHELVES[i]!, 0);
      parts.push(g);
    }
    return parts;
  };

  const run = (y: number, x0: number, x1: number, h: number, tiltLast: boolean): THREE.BufferGeometry[] => {
    const parts: THREE.BufferGeometry[] = [];
    let x = x0;
    let i = 0;
    while (x < x1 - 0.04) {
      const w = 0.03 + ((i * 7919) % 5) * 0.008;
      const hh = h - ((i * 3571) % 4) * 0.02;
      if (tiltLast && x + w > x1 - 0.12) {
        const g = box(w, hh, D - 0.09, 0, hh / 2, 0);
        g.rotateZ(-0.42);
        g.translate(x + 0.05, y + 0.012, 0);
        parts.push(g);
        break;
      }
      parts.push(box(w, hh, D - 0.09, x + w / 2, y + hh / 2 + 0.012, 0));
      x += w + 0.004;
      i++;
    }
    return parts;
  };

  const empty = merge(carcase(-1));
  const filled = merge(carcase(-1));
  const filledBooks = merge([
    ...run(SHELVES[1]!, -0.4, 0.15, 0.24, true),
    ...run(SHELVES[2]!, -0.4, 0.4, 0.26, false),
    ...run(SHELVES[4]!, -0.1, 0.4, 0.22, true),
  ]);

  const sagged = merge(carcase(2));
  const saggedBooks = merge([
    ...run(SHELVES[0]!, -0.4, 0.42, 0.22, false),
    // What came off the shelf that gave way.
    ybox(0.16, 0.04, 0.22, -0.2, 0.12, -0.04, 0.5),
    ybox(0.14, 0.04, 0.2, 0.02, 0.12, 0.02, -0.3),
    ybox(0.15, 0.16, 0.04, 0.24, 0.18, -0.02, 0.2),
  ]);

  return makeShape(spec, { kind: 'floor', yawJitter: 0.03 }, [
    layer(matte(pal.wood), [empty, filled, sagged]),
    layer(matte(pal.fabric), [null, filledBooks, saggedBooks]),
  ]);
}

/**
 * A full-length mirror, leaned against the wall.
 *
 * It does not reflect. A real reflection doubles the room, doubles the draw and
 * — worst of all — proves there is nothing behind you. Flat, dark, slightly
 * green glass reads as a mirror in this light and keeps the question open.
 */
function buildMirror(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const W = 0.66;
  const H = 1.66;
  const F = 0.055;

  const frame = (): THREE.BufferGeometry[] => [
    box(F, H, 0.05, -(W - F) / 2, H / 2, 0),
    box(F, H, 0.05, (W - F) / 2, H / 2, 0),
    box(W, F, 0.05, 0, H - F / 2, 0),
    box(W, F, 0.05, 0, F / 2, 0),
    box(W - F, H - F, 0.012, 0, H / 2, 0.018),
  ];
  const glassOf = (): THREE.BufferGeometry => box(W - F * 2, H - F * 2, 0.008, 0, H / 2, -0.012);

  const cleanF = frame();
  const cleanG = [glassOf()];
  lean([...cleanF, ...cleanG], 0.09, -0.06);

  const crackedF = frame();
  const crackedG = [glassOf()];
  const cracks = [
    (() => {
      const g = box(0.008, H * 0.7, 0.004);
      g.rotateZ(0.22);
      g.translate(0.06, H / 2, -0.018);
      return g;
    })(),
    (() => {
      const g = box(0.006, 0.4, 0.004);
      g.rotateZ(-0.7);
      g.translate(-0.1, H * 0.62, -0.018);
      return g;
    })(),
  ];
  lean([...crackedF, ...crackedG, ...cracks], 0.11, -0.05);

  // The glass is gone. What is left is the backing board and the frame.
  const goneF = frame();
  lean(goneF, 0.07, -0.07);

  return makeShape(spec, { kind: 'floor', yawJitter: 0.07 }, [
    layer(matte(pal.woodDark), [merge(cleanF), merge(crackedF), merge(goneF)]),
    layer(matte(belong(0x18231f, pal, pal.pull * 0.3, pal.gain * 0.85), 0.03), [merge(cleanG), merge(crackedG), null]),
    layer(matte(pal.pale, 0.08), [null, merge(cracks), null]),
  ]);
}

/** A pot plant that died a long time before anything else did. */
function buildDeadPlant(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);

  const pot = (): THREE.BufferGeometry[] => [
    cyl(0.2, 0.145, 0.34, 10, 0, 0.17, 0),
    cyl(0.215, 0.215, 0.03, 10, 0, 0.345, 0),
  ];
  const soil = (): THREE.BufferGeometry => cyl(0.185, 0.185, 0.03, 10, 0, 0.325, 0);

  const twigs = (seed: number, n: number, top: number): THREE.BufferGeometry[] => {
    const rng = makeRNG(seed);
    const parts: THREE.BufferGeometry[] = [];
    parts.push(strut(0.022, 0, 0.32, 0, rng.range(-0.03, 0.03), top * 0.6, rng.range(-0.03, 0.03), 5));
    for (let i = 0; i < n; i++) {
      const a = rng.range(0, Math.PI * 2);
      const r = rng.range(0.1, 0.2);
      const y0 = rng.range(0.42, top * 0.62);
      parts.push(strut(0.011, 0, y0, 0, Math.cos(a) * r, y0 + rng.range(0.08, 0.3), Math.sin(a) * r, 4));
    }
    return parts;
  };

  /** A dracaena that has dried out but is still, technically, in leaf. */
  const strapLeaves = (): THREE.BufferGeometry[] => [
    strut(0.035, 0, 0.32, 0, 0.02, 0.66, -0.01, 6),
    ...[0.4, 1.6, 2.7, 3.9, 5.2].map((a, i) => {
      // Built hanging off the origin, so the rotations pivot at the crown.
      const g = box(0.055, 0.4, 0.008, 0, -0.2, 0);
      g.rotateX(0.5 + (i % 3) * 0.18);
      g.rotateY(a);
      g.translate(0, 0.72, 0);
      return g;
    }),
  ];

  return makeShape(spec, { kind: 'floor', yawJitter: Math.PI, tilt: 0.02 }, [
    layer(matte(pal.plastic), [merge(pot()), merge(pot()), merge(pot())]),
    layer(matte(pal.woodDark), [
      merge(twigs(0x71a3, 6, 1.05)),
      merge(strapLeaves()),
      // Whatever was in this one snapped off at the soil.
      merge([strut(0.024, 0, 0.32, 0, 0.03, 0.52, -0.02, 5)]),
    ]),
    layer(matte(pal.dark, 0.02), [soil(), soil(), soil()]),
  ]);
}

/** A bed frame with nothing on it. */
function buildBedFrame(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const HW = 0.46;
  const HL = 0.94;

  const frame = (headH: number, footH: number): THREE.BufferGeometry[] => {
    const parts = [
      box(0.05, 0.3, 0.05, -HW, 0.15, -HL),
      box(0.05, 0.3, 0.05, HW, 0.15, -HL),
      box(0.05, 0.3, 0.05, -HW, 0.15, HL),
      box(0.05, 0.3, 0.05, HW, 0.15, HL),
      box(0.05, 0.11, HL * 2, -HW, 0.35, 0),
      box(0.05, 0.11, HL * 2, HW, 0.35, 0),
      box(HW * 2, 0.11, 0.05, 0, 0.35, -HL),
      box(HW * 2, 0.11, 0.05, 0, 0.35, HL),
    ];
    for (const [z, h] of [
      [HL, headH],
      [-HL, footH],
    ] as const) {
      parts.push(box(0.05, h, 0.05, -HW, 0.3 + h / 2, z), box(0.05, h, 0.05, HW, 0.3 + h / 2, z), box(HW * 2, 0.045, 0.045, 0, 0.3 + h, z));
      for (const x of [-0.22, 0, 0.22]) parts.push(box(0.026, h - 0.04, 0.026, x, 0.3 + h / 2, z));
    }
    return parts;
  };
  const slats = (n: number): THREE.BufferGeometry[] => {
    const parts: THREE.BufferGeometry[] = [];
    for (let i = 0; i < n; i++) {
      parts.push(box(HW * 2 - 0.04, 0.016, 0.07, 0, 0.4, -HL + 0.16 + (i * (HL * 2 - 0.32)) / (n - 1)));
    }
    return parts;
  };

  const made = merge([...frame(0.32, 0.2), ...slats(8)]);
  const sagged = merge([...frame(0.32, 0.2), ...slats(6)]);
  // One leg has gone and it is sitting on the corner it lost.
  tumble([sagged], 0.018, 0.022);
  settle([sagged]);
  const stripped = merge(frame(0.32, 0.2));

  return makeShape(spec, { kind: 'floor', yawJitter: 0.05 }, solo(matte(pal.metalDark), [made, sagged, stripped]));
}

/** A two-seater sofa, well past the point where anybody would sit on it. */
function buildSofa(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const W = 1.84;
  const D = 0.84;

  const shell = (): THREE.BufferGeometry[] => [
    box(W, 0.28, D, 0, 0.26, 0),
    box(W, 0.42, 0.22, 0, 0.6, D / 2 - 0.11),
    box(0.2, 0.34, D, -(W / 2 - 0.1), 0.55, 0),
    box(0.2, 0.34, D, W / 2 - 0.1, 0.55, 0),
    blob(0.1, 0.06, D / 2 - 0.02, -(W / 2 - 0.1), 0.71, 0),
    blob(0.1, 0.06, D / 2 - 0.02, W / 2 - 0.1, 0.71, 0),
  ];
  const cushion = (x: number, drop: number): THREE.BufferGeometry =>
    box(W / 2 - 0.24, 0.14, D - 0.28, x, 0.47 - drop, -0.06);
  const backs = (): THREE.BufferGeometry[] => [
    box(W / 2 - 0.24, 0.32, 0.14, -(W / 4 - 0.06), 0.61, D / 2 - 0.24),
    box(W / 2 - 0.24, 0.32, 0.14, W / 4 - 0.06, 0.61, D / 2 - 0.24),
  ];
  const feet = (): THREE.BufferGeometry[] => {
    const parts: THREE.BufferGeometry[] = [];
    for (const sx of [-1, 1] as const) {
      for (const sz of [-1, 1] as const) {
        parts.push(box(0.06, 0.12, 0.06, sx * (W / 2 - 0.1), 0.06, sz * (D / 2 - 0.1)));
      }
    }
    return parts;
  };

  const whole = merge([...shell(), cushion(-(W / 4 - 0.06), 0), cushion(W / 4 - 0.06, 0), ...backs()]);
  const oneGone = merge([...shell(), cushion(-(W / 4 - 0.06), 0), ...backs()]);
  const bare = merge(shell());

  // The deck under a missing cushion, and the frame under a missing deck.
  const deck = merge([box(W / 2 - 0.2, 0.02, D - 0.24, W / 4 - 0.06, 0.4, -0.06)]);
  const springs = merge([
    box(W - 0.24, 0.02, D - 0.26, 0, 0.39, -0.04),
    ...[0, 1, 2, 3, 4].map((i) => box(0.02, 0.05, D - 0.3, -0.6 + i * 0.3, 0.42, -0.04)),
  ]);

  return makeShape(spec, { kind: 'floor', yawJitter: 0.05 }, [
    layer(matte(pal.fabric), [whole, oneGone, bare]),
    layer(matte(pal.dark), [null, deck, springs]),
    layer(matte(pal.woodDark), [merge(feet()), merge(feet()), merge(feet())]),
  ]);
}

/** A standard lamp. It has not worked in years and there is nothing to plug it into. */
function buildStandingLamp(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);

  const post = (): THREE.BufferGeometry[] => [
    cyl(0.16, 0.17, 0.025, 12, 0, 0.012, 0),
    cyl(0.05, 0.06, 0.04, 8, 0, 0.045, 0),
    cyl(0.014, 0.016, 1.36, 6, 0, 0.71, 0),
    // The flex, coiled at the foot. Kept on the base rather than run off across
    // the carpet, because the box that blocks the player is only the base.
    strut(0.006, 0.03, 0.01, 0.05, 0.13, 0.008, 0.11, 4),
    strut(0.006, 0.13, 0.008, 0.11, 0.02, 0.008, 0.14, 4),
  ];
  const holder = (): THREE.BufferGeometry[] => [cyl(0.022, 0.028, 0.07, 6, 0, 1.42, 0)];
  const bulb = (): THREE.BufferGeometry => blob(0.035, 0.05, 0.035, 0, 1.49, 0);
  const shadeOf = (tilt: number): THREE.BufferGeometry => {
    // Kept under the 0.36 the footprint blocks, so the shade is never wider
    // than the box the player is stopped by.
    const g = cyl(0.14, 0.175, 0.28, 12, 0, 0, 0, true);
    if (tilt) {
      g.rotateZ(tilt);
      g.rotateX(tilt * 0.4);
    }
    g.translate(0, 1.44, 0);
    return g;
  };

  const straight = merge([...post(), ...holder()]);
  const askew = merge([...post(), ...holder()]);
  const stripped = merge([...post(), ...holder(), cyl(0.006, 0.006, 0.14, 4, 0, 1.53, 0)]);

  return makeShape(spec, { kind: 'floor', yawJitter: Math.PI, tilt: 0.012 }, [
    layer(matte(pal.metalDark), [straight, askew, stripped]),
    layer(matte(pal.paper), [shadeOf(0), shadeOf(0.24), null]),
    layer(matte(pal.pale, 0.02), [bulb(), bulb(), bulb()]),
  ]);
}

/** A wall clock, stopped. What time it stopped at is the only thing about it. */
function buildWallClock(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const R = 0.17;
  const texture = makeClockFace(css(belong(0xe8e3d2, pal, pal.pull * 0.5, pal.gain)), css(pal.dark));

  // The bezel is a rim, so it frames the dial behind it instead of capping it.
  const body = (): THREE.BufferGeometry => merge([rodZ(R, 0.055, 14, 0, 0, -0.0275), tubeZ(R - 0.012, 0.012, 14, 0, 0, -0.062)]);

  const dial = (): THREE.BufferGeometry => {
    const g = new THREE.CircleGeometry(R - 0.018, 16);
    const uv = g.attributes.uv as THREE.BufferAttribute;
    // Turned to face the room, which mirrors X — so the dial's U is flipped back
    // or the numerals run anticlockwise.
    for (let i = 0; i < uv.count; i++) uv.setX(i, 1 - uv.getX(i));
    uv.needsUpdate = true;
    g.rotateY(Math.PI);
    g.translate(0, 0, -0.058);
    return g;
  };

  /** Hands at h:mm, built pointing at twelve and turned clockwise from there. */
  const hands = (h: number, m: number, minuteOnly: boolean): THREE.BufferGeometry => {
    const parts: THREE.BufferGeometry[] = [];
    if (!minuteOnly) {
      const hour = box(0.013, R * 0.62, 0.005, 0, R * 0.28, 0);
      hour.rotateZ(-((h % 12) / 12 + m / 720) * Math.PI * 2);
      parts.push(hour);
    }
    const min = box(0.01, R * 0.88, 0.005, 0, R * 0.41, 0);
    min.rotateZ(-(m / 60) * Math.PI * 2);
    parts.push(min, cyl(0.012, 0.012, 0.01, 6, 0, 0, 0).rotateX(Math.PI / 2));
    for (const g of parts) g.translate(0, 0, -0.062);
    return merge(parts);
  };

  return makeShape(
    spec,
    { kind: 'wall', height: 2.2, out: WALL_INSET, yawJitter: 0.03 },
    [
      layer(matte(pal.plasticDark), [body(), body(), body()]),
      layer(new THREE.MeshLambertMaterial({ map: texture }), [dial(), dial(), dial()]),
      layer(matte(pal.dark), [hands(4, 37, false), hands(11, 3, false), hands(6, 0, true)]),
    ],
    [texture]
  );
}

/** A washbasin, bolted to wallpaper. */
function buildSink(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const W = 0.6;
  const D = 0.46;
  const RIM = 0.85;

  /** An oval basin: a squashed cylinder, which is cheaper than a rounded box. */
  const oval = (rx: number, rz: number, h: number, y: number, z: number): THREE.BufferGeometry => {
    const g = cyl(rx, rx * 0.92, h, 12, 0, y, z);
    g.scale(1, 1, rz / rx);
    return g;
  };

  const basin = (): THREE.BufferGeometry[] => [
    oval(W / 2, D / 2 - 0.02, 0.16, RIM - 0.08, -0.02),
    // The splashback, and the two brackets holding all of it up.
    box(W - 0.1, 0.14, 0.04, 0, RIM - 0.02, D / 2 - 0.02),
    box(0.05, 0.2, 0.14, -(W / 2 - 0.12), RIM - 0.24, D / 2 - 0.09),
    box(0.05, 0.2, 0.14, W / 2 - 0.12, RIM - 0.24, D / 2 - 0.09),
  ];
  const bowl = (): THREE.BufferGeometry => oval(W / 2 - 0.07, D / 2 - 0.09, 0.05, RIM - 0.035, -0.03);

  const trap = (hanging: boolean): THREE.BufferGeometry[] => {
    if (hanging) {
      return [
        strut(0.022, 0, RIM - 0.16, -0.1, 0.02, 0.36, -0.16, 6),
        strut(0.022, 0.02, 0.36, -0.16, -0.12, 0.14, -0.2, 6),
      ];
    }
    return [
      strut(0.022, 0, RIM - 0.16, -0.14, 0, 0.5, -0.14, 6),
      cyl(0.05, 0.05, 0.07, 8, 0, 0.46, -0.14),
      strut(0.024, 0, 0.44, -0.14, 0, 0.4, 0.02, 6),
      strut(0.024, 0, 0.4, 0.02, 0, 0.04, 0.06, 6),
    ];
  };
  const tap = (): THREE.BufferGeometry[] => [
    cyl(0.024, 0.026, 0.12, 6, 0, RIM + 0.05, 0.12),
    strut(0.017, 0, RIM + 0.11, 0.12, 0, RIM + 0.13, -0.02, 5),
    cyl(0.03, 0.03, 0.026, 6, -0.1, RIM + 0.02, 0.14),
    cyl(0.03, 0.03, 0.026, 6, 0.1, RIM + 0.02, 0.14),
  ];

  const plumbed = merge(basin());
  const dry = merge(basin());
  const cracked = merge([...basin(), box(0.012, 0.16, 0.16, 0.12, RIM - 0.08, -0.12)]);

  return makeShape(spec, { kind: 'floor', yawJitter: 0.04 }, [
    layer(matte(pal.pale), [plumbed, dry, cracked]),
    layer(matte(pal.dark, 0.02), [bowl(), bowl(), bowl()]),
    layer(matte(pal.chrome, 0.08), [merge([...trap(false), ...tap()]), merge(trap(false)), merge(trap(true))]),
  ]);
}

/** Filing cabinets, stacked to the ceiling by somebody who needed the floor. */
function buildFilingTower(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const W = 0.5;
  const D = 0.58;
  const UNIT = 0.6;

  const unit = (y: number, dx: number, dz: number, yaw: number, pulled: number): { shell: THREE.BufferGeometry[]; dark: THREE.BufferGeometry[] } => {
    /** A point on the unit's front face, `out` metres proud of it. */
    const at = (out: number): { x: number; z: number } => {
      const f = faceOffset(yaw, D / 2 + out);
      return { x: dx + f.dx, z: dz + f.dz };
    };
    const lower = at(0.01);
    const upper = at(0.01 + pulled);
    const lowerPull = at(0.03);
    const upperPull = at(0.03 + pulled);

    const shell = [
      ybox(W, UNIT - 0.02, D, dx, y + UNIT / 2, dz, yaw),
      ybox(W - 0.06, 0.24, 0.02, lower.x, y + 0.16, lower.z, yaw),
      ybox(W - 0.06, 0.24, 0.02, upper.x, y + 0.42, upper.z, yaw),
    ];
    const dark = [
      ybox(0.16, 0.024, 0.026, lowerPull.x, y + 0.24, lowerPull.z, yaw),
      ybox(0.16, 0.024, 0.026, upperPull.x, y + 0.5, upperPull.z, yaw),
    ];
    if (pulled > 0) {
      // The drawer body behind the front that has been pulled out, and the dark
      // it came out of.
      const body = at(pulled - D * 0.3);
      dark.push(ybox(W - 0.08, 0.22, D * 0.6, body.x, y + 0.42, body.z, yaw));
    }
    return { shell, dark };
  };

  const build = (specs: ReadonlyArray<readonly [number, number, number, number]>): { shell: THREE.BufferGeometry[]; dark: THREE.BufferGeometry[] } => {
    const shell: THREE.BufferGeometry[] = [];
    const dark: THREE.BufferGeometry[] = [];
    for (let i = 0; i < specs.length; i++) {
      const [dx, dz, yaw, pulled] = specs[i]!;
      const u = unit(i * UNIT, dx, dz, yaw, pulled);
      shell.push(...u.shell);
      dark.push(...u.dark);
    }
    return { shell, dark };
  };

  const square = build([
    [0, 0, 0, 0],
    [0, 0, 0, 0],
    [0, 0, 0, 0],
    [0, 0, 0, 0],
  ]);
  const drifted = build([
    [0, 0, 0.02, 0],
    [0.015, -0.01, -0.04, 0],
    [-0.015, 0.015, 0.03, 0.05],
    [0.02, 0, -0.03, 0],
  ]);
  const turned = build([
    [0, 0, 0, 0],
    [0, 0, 0.02, 0],
    [0.01, -0.01, 0.08, 0],
  ]);

  return makeShape(spec, { kind: 'floor', yawJitter: 0.06 }, [
    layer(matte(pal.metal), [merge(square.shell), merge(drifted.shell), merge(turned.shell)]),
    layer(matte(pal.dark), [merge(square.dark), merge(drifted.dark), merge(turned.dark)]),
  ]);
}

/** Carpet, rolled, propped where somebody meant to lay it. */
function buildCarpetRoll(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const R = 0.19;
  const L = 1.76;

  const roll = (y: number, z: number, len: number): THREE.BufferGeometry => rodX(R, len, 10, 0, y, z);
  const core = (y: number, z: number, len: number): THREE.BufferGeometry =>
    merge([rodX(0.045, 0.02, 6, -len / 2 - 0.011, y, z), rodX(0.045, 0.02, 6, len / 2 + 0.011, y, z)]);

  const one = roll(R, 0, L);
  const oneCore = core(R, 0, L);

  const two = merge([roll(R, -0.04, L), roll(R + 0.33, 0.03, L - 0.14)]);
  const twoCore = merge([core(R, -0.04, L), core(R + 0.33, 0.03, L - 0.14)]);

  // One of them has been started and left with a flap of pile on the floor.
  const opened = merge([roll(R, 0, L), box(L, 0.014, 0.16, 0, 0.007, -0.11)]);
  const openedCore = core(R, 0, L);

  return makeShape(spec, { kind: 'floor', yawJitter: 0.05 }, [
    layer(matte(pal.fabric), [one, two, opened]),
    layer(matte(pal.dark, 0.02), [oneCore, twoCore, openedCore]),
  ]);
}

/** A fire hydrant, on carpet, four floors up. */
function buildHydrant(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);

  const barrel = (bonnet: boolean): THREE.BufferGeometry[] => {
    const parts = [
      cyl(0.15, 0.17, 0.05, 10, 0, 0.025, 0),
      cyl(0.105, 0.125, 0.5, 10, 0, 0.3, 0),
      cyl(0.135, 0.135, 0.045, 6, 0, 0.575, 0),
    ];
    if (bonnet) {
      parts.push(blob(0.115, 0.09, 0.115, 0, 0.62, 0), cyl(0.145, 0.145, 0.03, 10, 0, 0.7, 0), box(0.06, 0.09, 0.06, 0, 0.76, 0));
    }
    return parts;
  };
  const outlets = (capped: readonly boolean[]): { steel: THREE.BufferGeometry[]; caps: THREE.BufferGeometry[] } => {
    const steel: THREE.BufferGeometry[] = [];
    const caps: THREE.BufferGeometry[] = [];
    const spouts: ReadonlyArray<readonly ['x' | 'z', number]> = [
      ['z', -1],
      ['x', -1],
      ['x', 1],
    ];
    for (let i = 0; i < spouts.length; i++) {
      const [axis, sign] = spouts[i]!;
      const r = i === 0 ? 0.055 : 0.04;
      const reach = 0.145;
      if (axis === 'z') {
        steel.push(rodZ(r, 0.1, 8, 0, 0.4, sign * 0.11));
        if (capped[i]) caps.push(rodZ(r + 0.012, 0.03, 8, 0, 0.4, sign * reach));
      } else {
        steel.push(rodX(r, 0.1, 8, sign * 0.11, 0.44, 0));
        if (capped[i]) caps.push(rodX(r + 0.012, 0.03, 8, sign * reach, 0.44, 0));
      }
    }
    return { steel, caps };
  };

  const capped = outlets([true, true, true]);
  const one = outlets([false, true, true]);
  const flat = outlets([true, true, true]);

  const intact = merge([...barrel(true), ...capped.steel]);
  const opened = merge([...barrel(true), ...one.steel]);
  // Bonnet gone, and the barrel left open on a stub of thread.
  const bonnetless = merge([...barrel(false), ...flat.steel, cyl(0.115, 0.115, 0.02, 10, 0, 0.605, 0)]);

  const chain = merge([strut(0.007, 0, 0.4, -0.16, 0.03, 0.24, -0.2, 4), strut(0.007, 0.03, 0.24, -0.2, 0.02, 0.06, -0.17, 4)]);

  return makeShape(spec, { kind: 'floor', yawJitter: Math.PI }, [
    layer(matte(pal.hazard), [intact, opened, bonnetless]),
    layer(matte(pal.metalDark), [merge(capped.caps), merge([...one.caps, chain]), merge(flat.caps)]),
  ]);
}

/** A bicycle, indoors, a very long way from a road. */
function buildBicycle(spec: LevelSpec): PropShape {
  const pal = propPalette(spec);
  const WR = 0.31;
  const HUB = 0.33;

  const wheelPair = (front: boolean): THREE.BufferGeometry[] => {
    const parts = [ring(WR, 0.018, 14, -0.5, HUB, 0)];
    if (front) parts.push(ring(WR, 0.018, 14, 0.5, HUB, 0));
    return parts;
  };
  const frame = (front: boolean): THREE.BufferGeometry[] => {
    const parts = [
      strut(0.019, -0.5, HUB, 0, 0.02, 0.28, 0, 5),
      strut(0.019, -0.5, HUB, 0, -0.06, 0.78, 0, 5),
      strut(0.021, 0.02, 0.28, 0, -0.06, 0.78, 0, 5),
      strut(0.021, 0.02, 0.28, 0, 0.42, 0.84, 0, 5),
      strut(0.019, -0.06, 0.78, 0, 0.42, 0.84, 0, 5),
      // Saddle and post.
      strut(0.014, -0.06, 0.78, 0, -0.1, 0.9, 0, 5),
      box(0.24, 0.045, 0.09, -0.11, 0.93, 0),
      // Bars, kept narrow: the collision box in `PROP_DEFS` is only 0.32 deep
      // and a bike is the one kind here that wants more.
      strut(0.014, 0.42, 0.84, 0, 0.46, 0.98, 0, 5),
      rodZ(0.013, 0.3, 6, 0.46, 1.0, 0),
      ring(0.09, 0.012, 8, 0.02, 0.28, 0.03),
      rodZ(0.011, 0.3, 5, 0.02, 0.28, 0),
    ];
    if (front) parts.push(strut(0.017, 0.42, 0.84, 0, 0.5, HUB, 0, 5));
    else parts.push(strut(0.017, 0.42, 0.84, 0, 0.52, 0.24, 0, 5));
    return parts;
  };

  // Everything is authored at full size and then taken in a tenth, which is the
  // difference between an adult bike and something an adult could have owned
  // twenty years ago — and 20 cm less overhang past the box that blocks it.
  const shrink = (parts: readonly THREE.BufferGeometry[]): THREE.BufferGeometry[] => {
    for (const g of parts) g.scale(0.9, 0.9, 0.9);
    return parts as THREE.BufferGeometry[];
  };

  const leantW = shrink(wheelPair(true));
  const leantF = shrink(frame(true));
  lean([...leantW, ...leantF], 0.1, 0.02);

  const strippedW = shrink(wheelPair(false));
  const strippedF = shrink(frame(false));
  lean([...strippedW, ...strippedF], 0.14, 0.03);

  // Flat on the floor. Centred in Z afterwards, because a fallen bike is a
  // metre across and only lines up with its collision box in the middle.
  const downW = shrink(wheelPair(true));
  const downF = shrink(frame(true));
  const down = [...downW, ...downF];
  tumble(down, 1.46, 0.05, 0.12);
  settle(down);
  centreZ(down);

  return makeShape(spec, { kind: 'floor', yawJitter: 0.1 }, [
    layer(matte(pal.metalDark), [merge(leantW), merge(strippedW), merge(downW)]),
    layer(matte(pal.metal), [merge(leantF), merge(strippedF), merge(downF)]),
  ]);
}

/* ================================================================== *
 * Registry
 * ================================================================== */

/**
 * One reference-counted bundle per kind, per level. The bundles are lazy, so a
 * level that has no payphones never builds one.
 */
const KITS: Record<PropKind, SpecKit<PropShape>> = {
  chair: new SpecKit(buildChair),
  boxes: new SpecKit(buildBoxes),
  cabinet: new SpecKit(buildCabinet),
  bucket: new SpecKit(buildBucket),
  papers: new SpecKit(buildPapers),
  vending: new SpecKit(buildVending),
  payphone: new SpecKit(buildPayphone),
  'hanging-light': new SpecKit(buildHangingLight),
  'ceiling-stain': new SpecKit(buildCeilingStain),

  pallet: new SpecKit(buildPallet),
  crate: new SpecKit(buildCrate),
  shelving: new SpecKit(buildShelving),
  cone: new SpecKit(buildCone),
  drum: new SpecKit(buildDrum),
  handtruck: new SpecKit(buildHandtruck),

  'pipe-run': new SpecKit(buildPipeRun),
  valve: new SpecKit(buildValve),
  'junction-box': new SpecKit(buildJunctionBox),
  ladder: new SpecKit(buildLadder),
  puddle: new SpecKit(buildPuddle),

  'pool-ladder': new SpecKit(buildPoolLadder),
  bench: new SpecKit(buildBench),
  'float-ring': new SpecKit(buildFloatRing),
  drain: new SpecKit(buildDrain),
  towel: new SpecKit(buildTowel),

  graffiti: new SpecKit(buildGraffiti),
  'tally-marks': new SpecKit(buildTallyMarks),
  arrow: new SpecKit(buildArrow),
  sign: new SpecKit(buildSign),
  camp: new SpecKit(buildCamp),

  // Architecture that does not make sense.
  'half-wall': new SpecKit(buildHalfWall),
  'stub-wall': new SpecKit(buildStubWall),
  'free-doorframe': new SpecKit(buildFreeDoorframe),
  'false-door': new SpecKit(buildFalseDoor),
  soffit: new SpecKit(buildSoffit),
  'column-cluster': new SpecKit(buildColumnCluster),
  'stairs-nowhere': new SpecKit(buildStairsNowhere),
  partition: new SpecKit(buildPartition),
  'inner-window': new SpecKit(buildInnerWindow),

  // Objects that should not be here.
  bathtub: new SpecKit(buildBathtub),
  'tv-stack': new SpecKit(buildTvStack),
  'phone-booth': new SpecKit(buildPhoneBooth),
  'shopping-cart': new SpecKit(buildShoppingCart),
  mattress: new SpecKit(buildMattress),
  piano: new SpecKit(buildPiano),
  'fish-tank': new SpecKit(buildFishTank),
  chandelier: new SpecKit(buildChandelier),
  mannequin: new SpecKit(buildMannequin),
  'chair-pile': new SpecKit(buildChairPile),
  'door-pile': new SpecKit(buildDoorPile),
  desk: new SpecKit(buildDesk),
  bookshelf: new SpecKit(buildBookshelf),
  mirror: new SpecKit(buildMirror),
  'dead-plant': new SpecKit(buildDeadPlant),
  'bed-frame': new SpecKit(buildBedFrame),
  sofa: new SpecKit(buildSofa),
  'standing-lamp': new SpecKit(buildStandingLamp),
  'wall-clock': new SpecKit(buildWallClock),
  sink: new SpecKit(buildSink),
  'filing-tower': new SpecKit(buildFilingTower),
  'carpet-roll': new SpecKit(buildCarpetRoll),
  hydrant: new SpecKit(buildHydrant),
  bicycle: new SpecKit(buildBicycle),
  shoe: new SpecKit(buildShoe),

  // The way down. Big enough, and loud enough, to be found. See `propslide.ts`.
  slide: new SpecKit(buildSlide),

  // The Funrooms and Level 94. Built in propfun.ts / propsuburb.ts so the two
  // levels' content can be modelled without contending for this file.
  'play-structure': new SpecKit(buildPlayStructure),
  'play-slide': new SpecKit(buildPlaySlide),
  'ball-pit': new SpecKit(buildBallPit),
  'crawl-tube': new SpecKit(buildCrawlTube),
  'soft-blocks': new SpecKit(buildSoftBlocks),
  balloons: new SpecKit(buildBalloons),
  streamers: new SpecKit(buildStreamers),
  banner: new SpecKit(buildBanner),
  'party-table': new SpecKit(buildPartyTable),
  'arcade-cabinet': new SpecKit(buildArcadeCabinet),
  'bouncy-castle': new SpecKit(buildBouncyCastle),
  confetti: new SpecKit(buildConfetti),

  'picket-fence': new SpecKit(buildPicketFence),
  mailbox: new SpecKit(buildMailbox),
  'lamp-post': new SpecKit(buildLampPost),
  shrub: new SpecKit(buildShrub),
  'garden-tree': new SpecKit(buildGardenTree),
  'swing-set': new SpecKit(buildSwingSet),
  'parked-car': new SpecKit(buildParkedCar),
  'water-tower': new SpecKit(buildWaterTower),
  'laundry-line': new SpecKit(buildLaundryLine),

  // Fitted to a building rather than dropped on a spot; see `propsubhouse.ts`.
  'house-hill': new SpecKit(buildHouseHill),
  'house-roof': new SpecKit(buildHouseRoof),
  'house-trim': new SpecKit(buildHouseTrim),
};

/** One mesh per layer, all of them pointing at the variant the spot asked for. */
function instantiate(shape: PropShape): { object: THREE.Object3D; place(spot: PropSpot): void } {
  const meshes: THREE.Mesh[] = [];
  const layers: PropLayer[] = [];

  for (const l of shape.layers) {
    const first = l.variants.find((g) => g !== null) ?? null;
    if (!first) continue;
    const mesh = new THREE.Mesh(first, l.material);
    mesh.matrixAutoUpdate = false;
    meshes.push(mesh);
    layers.push(l);
  }

  const single = meshes.length === 1 ? meshes[0]! : null;
  const object: THREE.Object3D = single ?? new THREE.Group();
  if (!single) for (const mesh of meshes) object.add(mesh);

  const anchor: Anchor = shape.anchor;
  return {
    object,
    place(spot: PropSpot): void {
      const v = variantIndex(spot.variant, shape.count);
      for (let i = 0; i < meshes.length; i++) {
        const variants = layers[i]!.variants;
        const geo = variants[Math.min(v, variants.length - 1)] ?? null;
        const mesh = meshes[i]!;
        if (geo) {
          mesh.geometry = geo;
          mesh.visible = true;
        } else {
          mesh.visible = false;
        }
      }
      anchorProp(object, spot, anchor, shape.ceilingHeight, shape.top);
    },
  };
}

/**
 * Build the visual for one piece of scenery.
 *
 * The view owns nothing but its meshes: geometry, materials and canvases belong
 * to a per-level bundle shared with every other prop of the same kind, and are
 * freed when the last of them is disposed.
 */
export function createPropView(kind: PropKind, spec: LevelSpec): PropView {
  const kit = KITS[kind];
  const shape = kit.acquire(spec);
  const instance = instantiate(shape);
  let live = true;

  return {
    object: instance.object,
    place(spot: PropSpot): void {
      instance.place(spot);
    },
    dispose(): void {
      // A double dispose would drop somebody else's reference and free the kind
      // out from under every prop still standing.
      if (!live) return;
      live = false;
      instance.object.clear();
      kit.release(spec);
    },
  };
}
